// T1.4a — progression algorithms.
//
// Four pure functions per the research's "Architecture specification"
// (lines ~22-238) plus an attempt-aggregator that turns child_attempts
// rows into per-skill SkillState records. THE codified anti-decorative-
// progress functions: every visible signal on the recap (and, in T1.4b,
// the path home) flows through these.
//
//   "Every visible progress signal must be a deterministic function of
//    child_attempts and ProgressionState, never hand-painted UI decoration."
//
// Split: the aggregator is parameterized by an attempt array (pure,
// testable); the async wrapper fetches via attemptLogger. This lets the
// unit test exercise the aggregation logic without a Supabase round-trip.

import type {
  ChildAttempt,
} from "@/curriculum/types/adaptationTypes";
import type {
  AgeBand,
  MasteryBand,
  SkillState,
  Chapter,
} from "@/types/progression";
import { ageBandRow } from "@/adaptation/types";
import { fetchAllAttemptsForChild } from "./attemptLogger";
import { isBelowFloor, foundationRedirectSkillId, type PlacementFloor } from "./placementFloor";
import { computeStrandStatuses, sortStrandByAgeBand, type SkillStatusNode } from "./skillTreeStatus";
import { isIndependentAttempt } from "./canonicalEvidence";
import {
  isAutomatic,
  DEFAULT_MATH_AUTOMATICITY_MS,
  DEFAULT_READING_AUTOMATICITY_MS,
  DEFAULT_AUTOMATICITY_MIN_SAMPLES,
} from "./automaticity";
// AGE PLACEMENT FLOOR (Wave2-C): ageFloorBelowSkillIds turns the resolved
// selection age into the set of focal candidates that sit MORE than
// AGE_FLOOR_GAP bands below the child — buildTodaysMission de-prioritises
// (never removes) them so an older child is not served a far-below-age focal
// skill. See ageFloorBelowSkillIds JSDoc in parentPriors for the calibration.
// MA-86 mastery lift / MA-454: the SERVE walk consumes serveAboveBandSkillIds
// (terminal-band +1 tolerance) while the TREE keeps ageAboveBandSkillIds
// (MA-91) — see serveAboveBandSkillIds' JSDoc for the deliberate divergence.
import { ageFloorBelowSkillIds, serveAboveBandSkillIds, ageBandForStudent, ageImpliedMasteredSkillIds, prerequisitesSatisfied } from "./parentPriors";
// pickReviewSkill band-proximity bias (review-skill-proximity):
import { ageBandForSkill, audienceForSkill } from "./skillMeta";
import { AGE_BAND_ORDER } from "@/adaptation/types";
// MA-86 mastery lift: the child's own AGE-band rank (canonical shared
// primitive, MA-CL-AB) — aliased because this file's local `bandRank` is
// the MASTERY-band ranker.
import { bandRank as ageBandRank } from "@/engine/ageBand.ts";
// Select-to-assign cross-strand surfacing (#246 follow-up): reuse the
// SAME drop-rule helpers the focal-pick path uses (assignedSkills.ts) so
// "is this assigned skill still wanted?" is decided in exactly one place.
// isSkillIntroduced/isSkillMastered are the prune predicates; an assigned
// skill the kid has already met (>=1 attempt) or mastered no longer needs
// surfacing and is filtered out below.
import { isSkillIntroduced, isSkillMastered } from "./assignedSkills";
// MA-896: the ONE bridge from the tree's projection to the picker's skill
// state (a tree-secure skill whose delayed review has not passed). Stamped
// by the aggregator below, read by pickReviewSkill's due tier.
import { treeReviewPending, isTreeReviewPending, type ReviewBridgedSkillState } from "./reviewCandidates";

// ── Algorithms (research verbatim) ─────────────────────────────────────

/** Mastery band from attempt history. Deterministic — see research §
 *  Architecture specification > Progress semantics.
 *
 *  FLUENT = the honest "mastered" claim. The recap's "mastered" tile
 *  (countDurablyMasteredSkills) and the mascot-growth signal both key off
 *  fluent, so fluent must mean what a parent hears: the child can produce
 *  the fact accurately, has RETAINED it across days, AND answers it FAST
 *  (automatically) rather than slowly re-deriving it. Speed is the
 *  automaticity lever (src/lib/engine/automaticity.ts).
 *
 *  CONSERVATIVE CALIBRATION (never demote for missing data): the speed
 *  requirement is GATED ON EVIDENCE. With no / too-few timed correct
 *  samples (automaticitySampleCount < DEFAULT_AUTOMATICITY_MIN_SAMPLES) we
 *  do NOT judge speed at all and fluent behaves exactly as before — a kid
 *  is never knocked down to "secure" merely because we lack latency data.
 *  Once enough timed correct samples exist, fluent additionally requires
 *  isAutomatic(): a child who is accurate + retained but SLOW stays
 *  "secure" (real progress, not yet mastery) instead of being over-claimed
 *  as mastered.
 *
 *  Three-case truth table (given the retention-based fluent conditions
 *  already hold — acc>=0.85, >=2 sessions, <2 consecutiveFails,
 *  retentionConfidence>=0.70):
 *    (a) enough samples + fast  → fluent
 *    (b) enough samples + slow  → secure   (the new gate)
 *    (c) no / few samples       → fluent   (no regression)
 *
 *  CALIBRATION TARGET: the speed threshold (DEFAULT_MATH_AUTOMATICITY_MS)
 *  and the evidence floor (DEFAULT_AUTOMATICITY_MIN_SAMPLES) are tunable —
 *  these are the timed single-step math-fact skills, so we use the math
 *  default. Stays PURE: reads only fields off the passed SkillState. */
export function deriveMasteryBand(
  skill: SkillState,
  // Speed gate threshold. Reading items run slower than math facts, so the
  // aggregator passes the domain-appropriate value (math 4s / reading 8s).
  // Defaults to math to preserve existing pure callers.
  automaticityThresholdMs: number = DEFAULT_MATH_AUTOMATICITY_MS,
): MasteryBand {
  if (skill.sessionsSeen < 2) return "new";
  const acc = skill.correctTotal / Math.max(skill.attemptsTotal, 1);

  if (acc < 0.60) return "new";
  if (acc < 0.80) return "emerging";

  // secure requires demonstrated success in more than one session/day
  if (acc >= 0.85 && skill.sessionsSeen >= 2 && skill.consecutiveFails < 2) {
    if (skill.retentionConfidence >= 0.70) {
      // Retention-based fluent conditions hold. Now apply the conservative
      // automaticity gate: enforce speed ONLY when there is enough timed
      // evidence to judge it. No/low evidence → keep prior fluent behavior;
      // enough evidence + slow → secure.
      const samples = skill.automaticitySampleCount ?? 0;
      const hasSpeedEvidence = samples >= DEFAULT_AUTOMATICITY_MIN_SAMPLES;
      const fastEnough = isAutomatic(skill, { thresholdMs: automaticityThresholdMs });
      if (!hasSpeedEvidence || fastEnough) return "fluent";
      return "secure";
    }
    return "secure";
  }

  return "emerging";
}

// ── Session-level mastery tier (P-MILESTONE-DERIVATION) ────────────────
//
// CALIBRATION-FROM-BRIEF (not research-cited): the 0.85 accuracy floor and
// the "no disqualifying hints" rule come from the P-MILESTONE-DERIVATION
// task brief, NOT a cited study or the (currently-unavailable) reward-cadence
// research doc. Treat as a tuning target — revisit when the research lands.
//
// This is a per-SESSION signal (one session's correct/total/hints), distinct
// from deriveMasteryBand's per-SKILL band (which integrates history across
// sessions). It feeds context_data.mastery on the session_completed marker;
// dayMasteryTier (trailState) then requires all three strands to clear it
// before the day earns the "Special Edition" story signal.
//
// Hint policy is the CALLER's responsibility, kept out of this helper so it
// stays a pure numeric predicate: the caller passes `disqualifyingHints` =
// the count of attempts whose hint level exceeds the ceiling that strand
// allows. Math/Reading/Mixed have no in-session hints (reaching the recap
// means the coach never fired), so they pass 0. Foundation is scaffolded by
// design — it tolerates H1 and passes only the count of H2+ attempts.

interface MasteryTierOpts {
  /** Accuracy floor (default 0.85, the brief's threshold). */
  minAccuracy?: number;
}

/** True iff this session clears the mastery tier: accuracy at or above the
 *  floor AND no disqualifying hints. Pure. Guards degenerate input:
 *  total <= 0 (no questions answered) → false; correct clamped into
 *  [0, total] so a bad caller can't manufacture >100% accuracy. */
export function isMasteryTier(
  correct: number,
  total: number,
  disqualifyingHints: number,
  opts: MasteryTierOpts = {},
): boolean {
  const minAccuracy = opts.minAccuracy ?? 0.85;
  if (!Number.isFinite(total) || total <= 0) return false;
  if (!Number.isFinite(correct)) return false;
  if (!Number.isFinite(disqualifyingHints) || disqualifyingHints > 0) return false;
  const clamped = Math.max(0, Math.min(correct, total));
  return clamped / total >= minAccuracy;
}

/** MA-297 chapter-credit predicate for ONE skill — extracted (MA-86 mastery
 *  lift) from chapterComplete's inner `hasChapterCredit` closure, byte-for-
 *  byte behavior-preserving, so buildTodaysMission's mastery lift can ask
 *  the EXACT question chapterComplete asks ("does this skill hold chapter
 *  credit right now?") without a second, drift-prone re-implementation.
 *  `requireConfirm` is the caller's precomputed MA-297 gate
 *  (CONFIRMED_SECURE_CHAPTER_GATE && not-already-credited — see
 *  chapterComplete / buildTodaysMission for the two call sites' identical
 *  derivations). Pure. */
export function skillHasChapterCredit(
  skillId: string,
  states: Record<string, SkillState>,
  requireConfirm: boolean,
): boolean {
  const s = states[skillId];
  if (!s) return false;
  if (!["secure", "fluent"].includes(s.masteryBand)) return false;
  // MA-297 RED-review fix: a SCAFFOLDED-ONLY skill (audienceForSkill ≠
  // independent_practice — ADR-055's ms.read.04/07 class) can NEVER earn
  // the confirm: every attempt it can produce carries audienceMode
  // "scaffolded", which isIndependentAttempt rejects by design. Requiring
  // an unearnable confirm would permanently discount the child's genuine
  // Foundation work and silently strip ch1/ch2's support-coverage margin
  // for the launch cohort. So the confirm is required only where it is
  // EARNABLE — the exact analogue of D-RC-4's un-servable-milestone
  // exclusion. Band membership still gates either way.
  if (requireConfirm && audienceForSkill(skillId) === "independent_practice") {
    return s.confirmedSecure === true;
  }
  return true;
}

/** Chapter is complete when its milestone bundle is secure AND >=70% of
 *  the chapter's support skills are secure AND the checkpoint passed.
 *  Research § Chapter boundary rule.
 *
 *  Order note: skillHasChapterCredit (immediately above) is this
 *  function's extracted inner credit predicate — buildTodaysMission's
 *  MA-86 mastery lift shares it so "credited" means the same thing in
 *  both places by construction.
 *
 *  D-RC-4 zero-pool milestone gate (§K fold addendum 2026-06-12 §D,
 *  LOCKED option (a) — self-healing): when the optional `poolSizeFor`
 *  is provided, a milestone gates completion ONLY if its served pool is
 *  non-zero. A zero-pool milestone is EXCLUDED from the predicate — this
 *  is what keeps ch1 completable while a ch1 milestone has no content at
 *  all. Without this, ch1 can never complete, engine advancement never
 *  reaches ch2/ch3, and ms.read.06 is reachable only via fallback.
 *  UPDATED (MA-671 slice 1b, 2026-09-19): ms.read.02 (Letter sounds) is no
 *  longer an example of a zero-pool milestone — it was routed to a real
 *  30-item pool (foundationItems/FoundationSession) and now genuinely
 *  gates ch1 like any other milestone, self-healing exactly as the
 *  paragraph below describes. See RED's MA-671b review, case E, for the
 *  one child shape this re-binds (a 4.5-5.0yo, ch1-complete on the
 *  exclusion, ms.read.04 not secure): self-clearing, disclosed in
 *  BACKLOG's MA-671 decision-log line and the PR body.
 *
 *  Deliberate pedagogy trade-off (founder-delegated): a kid can complete
 *  ch1 WITHOUT phoneme blending — but only WHILE the product cannot
 *  teach it at all (zero servable items). SELF-HEALING is the point:
 *  completion is re-derived on every call with a live `poolSizeFor`, so
 *  the moment content/routing gives the skill a non-zero pool it
 *  re-binds automatically as a required milestone — no migration, no
 *  flag flip. For a kid who hasn't completed the chapter, the milestone
 *  simply joins the gate. For a kid who advanced past the chapter under
 *  the exclusion, the re-bind CAN flip the chapter back to incomplete
 *  (completion is derived, never persisted) — the trail then routes them
 *  back to the now-teachable milestone, which is intended: the skill was
 *  never actually taught.
 *
 *  Scope: milestones ONLY (per D-RC-4). Support skills are NOT
 *  pool-gated — the 70% coverage threshold already absorbs zero-pool
 *  supports. If EVERY milestone is zero-pool the milestone gate is
 *  vacuously satisfied and completion rests on support coverage alone
 *  UNLESS the child has not yet aged past the chapter (MA-88 guard below).
 *  Absent `poolSizeFor` → prior behavior exactly (total function; legacy
 *  callers unchanged). Pure either way.
 *
 *  MA-88 vacuous-completion guard: when ALL milestones are zero-pool and
 *  the chapter has milestones, vacuous completion is blocked UNLESS the
 *  child has genuinely aged past every milestone (ageImpliedMasteredSkillIds,
 *  AGE_MASTERY_GAP=4). An on-band child with zero-pool milestones should
 *  wait for content or age out — not advance via vacuous support-coverage.
 *  Escape valve: when content is eventually authored (pool > 0) the
 *  milestone re-binds via D-RC-4's self-healing. When the child ages far
 *  enough past the chapter, `isMasterySkipped` covers all milestones and
 *  vacuous completion re-engages. */
export function chapterComplete(
  ch: Chapter,
  states: Record<string, SkillState>,
  poolSizeFor?: (skillId: string) => number,
  /** MA-88: optional age-mastery predicate. When provided and all milestones
   *  are zero-pool, vacuous completion is blocked for any milestone not
   *  mastery-skipped by age. Pass `(id) => masteredSet.has(id)` from
   *  ageImpliedMasteredSkillIds. Absent → no guard (backward compat). */
  isMasterySkipped?: (skillId: string) => boolean,
  /** MA-246 (ADR-068): true when this exact (child, chapter) pair has a
   *  PERSISTED `chapter_complete` milestone event on record — i.e. support
   *  coverage was observed >=70% at least once before. Freezes ONLY the
   *  support-coverage term at "met" so a LATER curriculum edit that GROWS
   *  `ch.supportSkills` (e.g. 5→6 entries) can never retroactively drop an
   *  already-credited chapter below the bar (the MA-218 regression class —
   *  a child who legitimately cleared the old bar must not be silently
   *  un-completed and walked backward by buildTodaysMission). Deliberately
   *  does NOT touch `milestoneSecure` — D-RC-4's self-heal (a milestone
   *  that becomes newly servable re-binds and can re-gate the chapter) stays
   *  fully live either way. Absent/false → current live-recompute behavior
   *  exactly (back-compat). */
  supportCoverageCredited?: boolean,
): boolean {
  // D-RC-4: only milestones the content pipeline can actually serve
  // participate in the gate.
  const gatingMilestones = poolSizeFor
    ? ch.milestoneSkills.filter((k) => poolSizeFor(k) > 0)
    : ch.milestoneSkills;

  // MA-88: when all milestones are zero-pool (gatingMilestones=[]) and the
  // chapter has milestones, block vacuous completion unless the child has
  // aged far enough past every milestone (AGE_MASTERY_GAP=4 bands below).
  if (
    poolSizeFor &&
    isMasterySkipped &&
    gatingMilestones.length === 0 &&
    ch.milestoneSkills.length > 0 &&
    ch.milestoneSkills.some((k) => !isMasterySkipped(k))
  ) {
    return false;
  }

  // MA-297 ("Mastered = confirmed on a later day", MA-DEC-0721 #1, ADR-087):
  // chapter credit now requires the CROSS-DAY confirm on top of band
  // membership — a skill secured in a single sitting does not advance the
  // chapter until one independent correct lands on a later calendar day
  // (deriveConfirmedSecure). Applies to milestones AND support coverage.
  // Deliberately strict: `fluent` does NOT bypass the confirm — after a
  // demote-and-re-reach, stale cross-day history must not stand in for a
  // fresh confirm (the confirm day is anchored to the most recent re-reach).
  // TWO carve-outs, both fail-open toward already-earned credit:
  //   (a) revert lever — CONFIRMED_SECURE_CHAPTER_GATE=false restores the
  //       bare band predicate (one-line revert per MA-DEC-0721 kill criteria);
  //   (b) ratchet (ADR-068 extension) — a chapter with a PERSISTED
  //       chapter_complete credit (`supportCoverageCredited`) is never
  //       retro-un-completed by this gate (the MA-218 walk-backward class;
  //       historical skills can lack post-hoc confirms through no fault of
  //       the child). D-RC-4's milestone re-bind self-heal is unchanged —
  //       band membership is still live-recomputed below either way.
  const requireConfirm =
    CONFIRMED_SECURE_CHAPTER_GATE && supportCoverageCredited !== true;
  const hasChapterCredit = (k: string): boolean =>
    skillHasChapterCredit(k, states, requireConfirm);

  const milestoneSecure = gatingMilestones.every(hasChapterCredit);

  const supportCoverage =
    ch.supportSkills.filter(hasChapterCredit).length /
    Math.max(ch.supportSkills.length, 1);
  const supportGateMet = supportCoverageCredited === true || supportCoverage >= 0.70;

  // T1.4a: the chapter-gate stone outcome is wired in T1.4b. Until then we
  // treat the checkpoint as passed; the engine still gates on milestone +
  // coverage, which is the load-bearing test.
  const checkpointPassed = true;

  return milestoneSecure && supportGateMet && checkpointPassed;
}

/** Pacing signal off a single skill's state. Research § Pacing and
 *  recovery logic. */
export function updatePacingFlag(
  skill: SkillState,
): "on_track" | "too_fast" | "too_slow" | "plateau" {
  if (skill.consecutiveFails >= 3) return "plateau";
  if (skill.masteryBand === "fluent" && skill.consecutivePasses >= 3) return "too_fast";
  if (skill.masteryBand === "emerging" && skill.sessionsSeen >= 5) return "too_slow";
  return "on_track";
}

// ── Aggregator (attempt history → SkillState per skill) ────────────────

/** Day key (YYYY-MM-DD) for the spaced-review heuristic. */
function dayKey(iso: string): string {
  // Cheap + deterministic; uses local TZ. Sufficient for "different day"
  // semantics — the engine doesn't depend on minute-level boundaries.
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Session key: prefer sessionId, fall back to runId (standalone attempts).
 *  Matches buildAttemptWindow's standalone-grouping convention. */
function sessionKey(a: ChildAttempt): string {
  return a.sessionId ?? a.runId ?? `t:${a.timestamp}`;
}

// MA-299 (MA-DEC-0721 #3): new-skill soft cap — "≤2 skills newly entering
// practice per strand-day". The daily quota buildTodaysMission's ranker
// enforces per strand (math/reading) via strandForSkill.
export const NEW_SKILL_DAILY_CAP = 2;

/** Skill ids "newly introduced today": every attempt for the skill in
 *  `attempts` falls on `now`'s local calendar day (dayKey) — i.e. the skill
 *  had ZERO attempts before today, within the fetched attempt window.
 *  CAVEAT (same window limit as computeRetentionConfidence): callers cap
 *  `attempts` (e.g. fetchAllAttemptsForChild's 500-row newest window) — a
 *  skill whose only pre-today attempts fall outside that window would be
 *  mis-counted as new-today. Pure; no I/O. */
export function newlyIntroducedSkillIdsToday(
  attempts: ChildAttempt[],
  now: Date = new Date(),
): Set<string> {
  const today = dayKey(now.toISOString());
  const allAttemptsToday = new Map<string, boolean>();
  for (const a of attempts) {
    const same = dayKey(a.timestamp) === today;
    allAttemptsToday.set(a.skillId, (allAttemptsToday.get(a.skillId) ?? true) && same);
  }
  const out = new Set<string>();
  for (const [skillId, onlyToday] of allAttemptsToday) if (onlyToday) out.add(skillId);
  return out;
}

/** retentionConfidence (0..1) — fraction of cross-day attempts that were
 *  correct, among attempts whose prior same-skill attempt was on a different
 *  calendar day. <2 cross-day attempts → 0 (no retention evidence yet).
 *  CALIBRATION TARGET: the research doesn't pin a formula; this captures
 *  "preserved over days" without overfitting. */
function computeRetentionConfidence(attempts: ChildAttempt[]): number {
  if (attempts.length < 2) return 0;
  let crossDayCorrect = 0;
  let crossDayTotal = 0;
  for (let i = 1; i < attempts.length; i++) {
    const prev = attempts[i - 1];
    const cur = attempts[i];
    if (dayKey(prev.timestamp) === dayKey(cur.timestamp)) continue;
    // BACKLOG 1.3 / G9: only an INDEPENDENT cross-day attempt is admissible
    // retention evidence. Scaffolded Foundation attempts, hinted attempts, and
    // within-session repeats are excluded — otherwise scaffolded help inflated
    // retentionConfidence, pushing secure->fluent (deriveMasteryBand) and
    // overstating mastery to parents. The PRIOR attempt only fixes the cross-day
    // boundary, so it need not be independent. (Legacy pre-0010 rows read
    // audienceMode=undefined -> excluded -> understates, never inflates.)
    if (!isIndependentAttempt(cur)) continue;
    crossDayTotal += 1;
    if (cur.correct) crossDayCorrect += 1;
  }
  if (crossDayTotal < 2) return 0;
  return crossDayCorrect / crossDayTotal;
}

// ── MA-297: the next-day frontier gate ("Mastered = confirmed on a later
// day", MA-DEC-0721; design record = MEMO-adaptation-engine-2026-07-20.md
// §4 edit 1 + ADR-087) ────────────────────────────────────────────────
//
// REVERT LEVER (single flag, PLACEMENT_FLOOR_ENABLED pattern): flip to
// false to restore the pre-MA-297 chapterComplete predicate (bare band
// membership) without a revert. Governed by MA-DEC-0721 #1's kill
// criteria: +1d confirm pass-rate >95% sustained ≥3 weeks ⇒ flip false;
// <50% sustained ⇒ keep + widen the evidence window.
export const CONFIRMED_SECURE_CHAPTER_GATE = true;

/** MA-297 — derive the cross-day confirm for one skill's chronological
 *  attempt list.
 *
 *  confirmedSecure := secureProvisional(now) && ∃ attempt a:
 *      a.correct ∧ isIndependentAttempt(a)
 *      ∧ dayKey(a.timestamp) > dayKey(t_reach)
 *
 *  where secureProvisional is EXACTLY deriveMasteryBand's secure-branch
 *  condition (all-time acc ≥ 0.85 ∧ sessionsSeen ≥ 2 ∧ consecutiveFails
 *  < 2 — same math, replayed incrementally), and t_reach = the LAST
 *  attempt of the MOST RECENT session in which that condition became
 *  true. Recomputed transitions handle the demote-and-re-reach cycle:
 *  2 consecutive fails (or accuracy sag) drop the condition; when it is
 *  re-reached later, t_reach moves FORWARD to the re-reach session, so a
 *  stale pre-demotion confirm can never satisfy the gate — after a
 *  demote, the skill must be re-confirmed on a day after the re-reach.
 *
 *  The confirm attempt itself: independent (audienceMode
 *  independent_practice, first attempt, H0, no supports —
 *  isIndependentAttempt, the same bar retentionConfidence uses) and on a
 *  strictly LATER calendar day (local-TZ dayKey; a later-same-day second
 *  session does NOT confirm — that is the same-day binge this gate
 *  exists to throttle). A session spanning midnight anchors on its LAST
 *  attempt's day, so a cross-midnight run can't self-confirm.
 *
 *  Derived on every aggregation from the attempt log — no persisted
 *  state, exactly like every other SkillState field. Pure.
 *
 *  MA-297 (live-verify batch-2): EXPORTED (was module-private) so
 *  useSkillGraph.ts can stamp per-skill `confirmedSecure` for the
 *  /parent/tree data-confirm-gate live-verify hook with THIS function —
 *  export-only, zero behavior change; a second confirm predicate must
 *  never exist (§00b; the same reuse-the-real-thing discipline
 *  scenarioContract.mjs follows for trustGate). Callers must pass the
 *  skill's attempts in chronological order (oldest → newest), exactly as
 *  aggregateSkillStatesFromAttempts below sorts them. */
export function deriveConfirmedSecure(skillAttempts: ChildAttempt[]): boolean {
  if (skillAttempts.length === 0) return false;
  const reach = secureReach(skillAttempts);
  // Not currently at the secure bar → nothing to confirm.
  if (reach === null) return false;
  const tReachDay = reach.day;
  return skillAttempts.some(
    (a) =>
      a.correct &&
      isIndependentAttempt(a) &&
      dayKey(a.timestamp) > tReachDay,
  );
}

/** MA-297 internals, shared by deriveConfirmedSecure and (MA-300)
 *  confirmAttemptOutcome so the confirm rule lives in ONE place: replay the
 *  secure-branch condition over a chronological attempt list and return
 *  the reach: `day` = dayKey(t_reach), the local day of the LAST attempt of
 *  the most recent session in which the condition became true, and
 *  `session` = that session's sessionKey — or null when the condition does
 *  not hold at the end of the list (nothing to confirm). Moved verbatim out
 *  of deriveConfirmedSecure (MA-300); behaviour unchanged. */
function secureReach(skillAttempts: ChildAttempt[]): { day: string; session: string } | null {
  let correct = 0;
  const sessions = new Set<string>();
  let consecFails = 0;
  let holdsPrev = false;
  let lastTransitionIdx = -1;
  for (let i = 0; i < skillAttempts.length; i++) {
    const a = skillAttempts[i];
    sessions.add(sessionKey(a));
    if (a.correct) {
      correct += 1;
      consecFails = 0;
    } else {
      consecFails += 1;
    }
    const acc = correct / (i + 1);
    const holds = sessions.size >= 2 && acc >= 0.85 && consecFails < 2;
    if (holds && !holdsPrev) lastTransitionIdx = i;
    holdsPrev = holds;
  }
  // Not currently at the secure bar → nothing to confirm.
  if (!holdsPrev || lastTransitionIdx < 0) return null;
  // t_reach = last attempt of the transition session (list is chronological).
  const reachSession = sessionKey(skillAttempts[lastTransitionIdx]);
  let tReachDay = dayKey(skillAttempts[lastTransitionIdx].timestamp);
  for (let j = lastTransitionIdx + 1; j < skillAttempts.length; j++) {
    if (sessionKey(skillAttempts[j]) === reachSession) {
      tReachDay = dayKey(skillAttempts[j].timestamp);
    }
  }
  return { day: tReachDay, session: reachSession };
}

/** MA-300 — what a just-built attempt means for the MA-297 next-day gate,
 *  decided at the moment the app records it, over the SAME internals as
 *  deriveConfirmedSecure (secureReach + isIndependentAttempt + dayKey +
 *  sessionKey — never a second confirm rule).
 *
 *  `priorSkillAttempts` = this skill's attempts BEFORE `attempt`, oldest →
 *  newest (the deriveConfirmedSecure input contract). The attempt is "the
 *  reach's first next-day independent attempt" when ALL hold:
 *    1. the secure condition holds over the prior list (a reach exists,
 *       secureReach non-null);
 *    2. `attempt` is independent (isIndependentAttempt), on a strictly
 *       later local day than t_reach, and NOT part of the reach session
 *       itself (a session spanning midnight moves t_reach forward in
 *       deriveConfirmedSecure, so it can never self-confirm — same here);
 *    3. no PRIOR attempt is independent and on a later day than t_reach —
 *       so the first one decides, pass or fail, and a skill that is already
 *       confirmed (a prior later-day independent correct) never counts again.
 *  `passed` = attempt.correct (only meaningful when isConfirmAttempt).
 *  `reachDay` = dayKey(t_reach) when a reach exists, else null. Pure. */
export interface ConfirmAttemptOutcome {
  isConfirmAttempt: boolean;
  passed: boolean;
  reachDay: string | null;
}

/** MA-300 (RED S2) — whether a reach that confirmAttemptOutcome found in a
 *  mount-time attempt WINDOW is the skill's real reach, not an artifact of the
 *  window cutting off older history. With a full history (fewer rows than
 *  MOUNT_HISTORY_LIMIT) every reach is real. With a truncated window, the
 *  replay starts mid-history, so a skill that was ALREADY secure before the
 *  window shows a fresh window-local "reach" on its second windowed session.
 *  Such a reach is rejected when either sign is present:
 *    1. the skill's first windowed attempt falls on the window's oldest local
 *       day (its history very likely continues before the cut), or
 *    2. an attempt of the skill on or before the reach day was served in a
 *       spaced-review slot (pickReviewSkill only serves skills already
 *       secure/fluent — so the skill was secure before this "reach").
 *  RESIDUAL (stated beside the metric): a skill secure before the window that
 *  re-enters it later than the window's first day through focal/cross-strand
 *  practice only is still counted. Conservative the other way too: a genuine
 *  demote-and-re-reach of a reviewed skill is excluded. Pure. */
export function reachIsInWindow(args: {
  windowTruncated: boolean;
  windowOldestTimestamp: string | null;
  priorSkillAttempts: ChildAttempt[];
  reachDay: string;
}): boolean {
  if (!args.windowTruncated) return true;
  const first = args.priorSkillAttempts[0];
  if (!first) return false;
  if (args.windowOldestTimestamp !== null && dayKey(first.timestamp) <= dayKey(args.windowOldestTimestamp)) {
    return false;
  }
  return !args.priorSkillAttempts.some(
    (a) => a.adaptiveReason === "spaced_review" && dayKey(a.timestamp) <= args.reachDay,
  );
}

export function confirmAttemptOutcome(
  priorSkillAttempts: ChildAttempt[],
  attempt: ChildAttempt,
): ConfirmAttemptOutcome {
  const reach = secureReach(priorSkillAttempts);
  const reachDay = reach?.day ?? null;
  const no: ConfirmAttemptOutcome = { isConfirmAttempt: false, passed: false, reachDay };
  if (reach === null || reachDay === null) return no;
  if (!isIndependentAttempt(attempt) || !(dayKey(attempt.timestamp) > reachDay)) return no;
  if (sessionKey(attempt) === reach.session) return no;
  if (priorSkillAttempts.some((a) => isIndependentAttempt(a) && dayKey(a.timestamp) > reachDay)) return no;
  return { isConfirmAttempt: true, passed: attempt.correct === true, reachDay };
}

/** supportLevel from the most recent attempt's supportMode/hintLevel. */
function deriveSupportLevel(latest: ChildAttempt | undefined): SkillState["supportLevel"] {
  if (!latest) return "none";
  const hint = latest.hintLevel;
  if (hint === "H2") return "heavy";
  if (hint === "H1") return "light";
  return "none";
}

/** Pure aggregator: turn a chronological attempt array into per-skill
 *  SkillState records. Testable without Supabase. */
export function aggregateSkillStatesFromAttempts(
  attempts: ChildAttempt[],
): Record<string, SkillState> {
  // Ensure chronological order (oldest → newest).
  const sorted = attempts
    .slice()
    .sort((a, b) => a.timestamp.localeCompare(b.timestamp));

  const bySkill = new Map<string, ChildAttempt[]>();
  for (const a of sorted) {
    const arr = bySkill.get(a.skillId) ?? [];
    arr.push(a);
    bySkill.set(a.skillId, arr);
  }

  const out: Record<string, SkillState> = {};
  for (const [skillKey, skillAttempts] of bySkill) {
    const attemptsTotal = skillAttempts.length;
    const correctTotal = skillAttempts.reduce((n, a) => n + (a.correct ? 1 : 0), 0);

    const sessions = new Set<string>();
    let lastSeenAt: string | null = null;
    let lastPassedAt: string | null = null;
    for (const a of skillAttempts) {
      sessions.add(sessionKey(a));
      lastSeenAt = a.timestamp;
      if (a.correct) lastPassedAt = a.timestamp;
    }

    // consecutiveFails/Passes from the end of the chronological list.
    let consecutiveFails = 0;
    let consecutivePasses = 0;
    for (let i = skillAttempts.length - 1; i >= 0; i--) {
      if (skillAttempts[i].correct) break;
      consecutiveFails += 1;
    }
    for (let i = skillAttempts.length - 1; i >= 0; i--) {
      if (!skillAttempts[i].correct) break;
      consecutivePasses += 1;
    }

    const retentionConfidence = computeRetentionConfidence(skillAttempts);
    const supportLevel = deriveSupportLevel(skillAttempts[skillAttempts.length - 1]);

    // Automaticity evidence: median response latency over the most recent
    // CORRECT attempts (speed only counts when the answer was right). Window
    // of 10 keeps it responsive to the kid's current pace. Used by
    // automaticity.isAutomatic; null when there's no timed correct sample.
    const correctLatencies = skillAttempts
      .filter((a) => a.correct && Number.isFinite(a.latencyMs) && a.latencyMs > 0)
      .map((a) => a.latencyMs)
      .slice(-10);
    const automaticitySampleCount = correctLatencies.length;
    let medianCorrectLatencyMs: number | null = null;
    if (automaticitySampleCount > 0) {
      const sortedLat = [...correctLatencies].sort((x, y) => x - y);
      const mid = Math.floor(sortedLat.length / 2);
      medianCorrectLatencyMs =
        sortedLat.length % 2 === 1 ? sortedLat[mid] : (sortedLat[mid - 1] + sortedLat[mid]) / 2;
    }

    // Build a partial state, then compute masteryBand from it. deriveMasteryBand
    // reads only the fields below — order-safe.
    const partial: SkillState = {
      skillKey,
      attemptsTotal,
      correctTotal,
      sessionsSeen: sessions.size,
      lastSeenAt,
      lastPassedAt,
      consecutiveFails,
      consecutivePasses,
      supportLevel,
      masteryBand: "new",
      retentionConfidence,
      // MA-297: cross-day frontier credit, derived from the same
      // chronological list as every other field.
      confirmedSecure: deriveConfirmedSecure(skillAttempts),
      medianCorrectLatencyMs,
      automaticitySampleCount,
    };
    // REVIEW FIX (cross-domain): reading skills must use the reading speed
    // threshold, not the math 4s gate — else normal-paced readers are demoted
    // out of 'fluent'/'mastered'. SkillState carries no domain, so derive it
    // from the skill-id prefix (all skills are ms.read.* or ms.math.*).
    const automaticityThresholdMs = skillKey.startsWith("ms.read")
      ? DEFAULT_READING_AUTOMATICITY_MS
      : DEFAULT_MATH_AUTOMATICITY_MS;
    partial.masteryBand = deriveMasteryBand(partial, automaticityThresholdMs);
    // MA-896: the review picker's bridge stamp — set ONLY when true, so a
    // skill with no pending tree review keeps the exact pre-MA-896 shape.
    if (treeReviewPending(skillKey, skillAttempts)) {
      (partial as ReviewBridgedSkillState).treeReviewPending = true;
    }
    out[skillKey] = partial;
  }
  return out;
}

/** Async wrapper: fetch the child's attempts and aggregate. */
export async function aggregateSkillStates(
  childId: string,
): Promise<Record<string, SkillState>> {
  return (await aggregateSkillStatesWithAttempts(childId)).states;
}

/** The mount-time attempt window (newest N rows across all skills) that
 *  aggregateSkillStates reads — the history the MA-297 gate itself sees.
 *  MA-300 exports it so the confirm tracker can tell a full history from a
 *  truncated one (reachIsInWindow). Was the literal 500. */
export const MOUNT_HISTORY_LIMIT = 500;

/** MA-300 — aggregateSkillStates that ALSO hands back the attempt history
 *  it aggregated (one fetch), so a session screen can seed the confirm-event
 *  tracker (confirmAttemptTracker.ts) from the mount-time history instead of
 *  re-reading it. aggregateSkillStates delegates here; behaviour unchanged. */
export async function aggregateSkillStatesWithAttempts(
  childId: string,
): Promise<{ states: Record<string, SkillState>; attempts: ChildAttempt[] }> {
  if (!childId) return { states: {}, attempts: [] };
  const attempts = await fetchAllAttemptsForChild(childId, MOUNT_HISTORY_LIMIT);
  return { states: aggregateSkillStatesFromAttempts(attempts), attempts };
}

/** T1.4b: filter ChildAttempt[] to only path-stone attempts.
 *
 *  The chapter-unlock contract reads ONLY from path-stone completions
 *  (the canonical spine) — never from free-play, badges, diagnostic, or
 *  review attempts. Per the research's failure-mode rule that progress
 *  signals must derive from intentional path activity, not incidental
 *  practice.
 *
 *  Back-compat: attempts persisted before T1.4b have no `sessionSource`
 *  field. They are treated as "path" (the conservative interpretation —
 *  every existing attempt source up to and including T1.4a runs through
 *  the path; P40 introduces the first non-path writers). */
export function filterPathAttempts(attempts: ChildAttempt[]): ChildAttempt[] {
  return attempts.filter((a) => (a.sessionSource ?? "path") === "path");
}

/** Aggregate ONLY path attempts. Drives chapterComplete + buildTodaysMission.
 *  Mastery readers (recap's countMasteredSkills, parent insights) keep
 *  using aggregateSkillStates over all attempts — every attempt informs
 *  mastery, but only path attempts inform chapter progress. */
export function aggregatePathSkillStatesFromAttempts(
  attempts: ChildAttempt[],
): Record<string, SkillState> {
  const path = filterPathAttempts(attempts);
  const states = aggregateSkillStatesFromAttempts(path);
  // MA-901 (D-1013-9): stamp a focal skill that is still emerging and has been the strand's
  // focal on FOCAL_ROTATION_DAYS or more consecutive practice days. Set ONLY then (the MA-896
  // bridge-stamp pattern), so every other state keeps its exact shape.
  for (const [id, n] of Object.entries(stuckFocalRunDays(path))) {
    const st = states[id];
    if (st && st.masteryBand === "emerging" && n >= FOCAL_ROTATION_DAYS) {
      (st as FocalRunStampedSkillState).focalStuckDays = n;
    }
  }
  return states;
}

/** MA-901 (operator ruling D-1013-9): practice days on one emerging focal skill before the
 *  strand's Today pick rotates to the next-ranked focal skill for one day. */
export const FOCAL_ROTATION_DAYS = 15;

export type FocalRunStampedSkillState = SkillState & { focalStuckDays?: number };

/** MA-901: for each skill that was its strand's focal on the child's most recent practice day,
 *  the number of consecutive practice days of that strand (most recent backwards) on which it was
 *  the strand's focal. A strand's practice day is a local day with a focal-role path attempt in
 *  that strand (D-1013-13: days spent only in the other strand are skipped); its focal is the skill
 *  of that strand with the most focal-role attempts that day (ties: the first seen). */
export function stuckFocalRunDays(pathAttempts: ChildAttempt[]): Record<string, number> {
  // day -> strand -> skill -> focal-role attempt count
  const days = new Map<string, Map<string, Map<string, number>>>();
  for (const a of pathAttempts) {
    if (a.adaptiveReason !== "focal") continue;
    const d = dayKey(a.timestamp);
    if (!days.has(d)) days.set(d, new Map());
    const strand = strandForSkill(a.skillId);
    const byStrand = days.get(d)!;
    if (!byStrand.has(strand)) byStrand.set(strand, new Map());
    const m = byStrand.get(strand)!;
    m.set(a.skillId, (m.get(a.skillId) ?? 0) + 1);
  }
  const order = [...days.keys()].sort().reverse();
  const focalOn = (d: string, strand: string): string | null => {
    const m = days.get(d)?.get(strand);
    if (!m) return null;
    let best: string | null = null;
    let n = -1;
    for (const [id, c] of m) if (c > n) { best = id; n = c; }
    return best;
  };
  const out: Record<string, number> = {};
  const strands = new Set<string>();
  for (const byStrand of days.values()) for (const st of byStrand.keys()) strands.add(st);
  for (const strand of strands) {
    // D-1013-13: only the strand's own practice days count; a day spent only in the other strand
    // neither ends nor extends the run.
    const own = order.filter((d) => days.get(d)!.has(strand));
    if (own.length === 0) continue;
    const x = focalOn(own[0], strand);
    if (!x) continue;
    let run = 0;
    for (const d of own) {
      if (focalOn(d, strand) !== x) break;
      run += 1;
    }
    out[x] = run;
  }
  return out;
}

// ── T1.4c: run structure (focal + spaced-review + cross-strand) ──────

/** The role each question in a session plays. Drives the interleave
 *  pattern + the parent-results explanation copy ("3 focal + 1 review
 *  + 1 cross-strand"). */
// MA-1004 (R2): the role values as a runtime list, so the session screens'
// data-slot-role and the capture driver's closed set import the SAME values.
export const SESSION_RUN_ROLES = Object.freeze(["focal", "spaced_review", "cross_strand"] as const);
export type SessionRunRole = (typeof SESSION_RUN_ROLES)[number];

export interface SessionRunQuestion {
  skillId: string;
  role: SessionRunRole;
  /** 0-indexed position in the run (matches MathSession/ReadingSession's
   *  `done` cursor). */
  ordinalInRun: number;
}

export interface SessionRun {
  questions: SessionRunQuestion[];
  focalSkillId: string;
  /** null when no qualifying secure-or-fluent skill in the focal's
   *  strand exists yet (fresh learner). */
  reviewSkillId: string | null;
  /** null when no qualifying secure-or-fluent skill in the OTHER strand
   *  exists yet. T1.4c same-strand-only wiring substitutes with focal
   *  but the field still surfaces the engine's pick for parent insights. */
  crossStrandSkillId: string | null;
  /** Count of role slots that were substituted with focal because no
   *  candidate qualified. Parent-results can surface "no variety today
   *  because still building review base." */
  substitutionsCount: number;
  /** #246 follow-up — assigned (parent-queued) skills that were surfaced in
   *  this run's EXTRA (spaced_review / cross_strand) slots, ahead of the
   *  normal secure/fluent picks. Empty when none qualified or no queue was
   *  passed. Order matches the queue head->tail. Surfaced for parent-results
   *  ("introducing the skills you lined up") and asserted by the e2e/unit
   *  round-trip. Does NOT include an assigned skill that drove the FOCAL pick
   *  (that path lives in useTodaysMission); this is strictly the extra-slot
   *  surfacing beyond focal. */
  assignedSurfacedSkillIds?: string[];
}

/** Subject (math vs reading) a skill belongs to. Reads the skillId
 *  prefix; matches PATH_SPEC's convention. ms.math.* → "math",
 *  ms.read.* → "reading". */
function strandForSkill(skillId: string): "math" | "reading" | "other" {
  if (skillId.startsWith("ms.math.")) return "math";
  if (skillId.startsWith("ms.read.")) return "reading";
  return "other";
}

/** Interleave pattern per sessionLength. Slot values:
 *    "F" = focal, "R" = spaced_review, "X" = cross_strand
 *  Returns slot array of length sessionLength. When a role's skill is
 *  null (no candidate), the caller substitutes "F" + bumps
 *  substitutionsCount.
 *
 *  PATTERN PRINCIPLE (P-SESSION-LENGTH-BUMP — patterns per Alex's product
 *  feedback, extrapolated from the T1.4c 3-8 density at ~20% review /
 *  ~15% cross-strand): front-load focal (positions 1 + 3-4 always focal —
 *  primes the session with success); insert review at position 2 then
 *  every ~5 positions; insert cross-strand at position 5 then every ~5
 *  positions, offset from review; tail with focal (ends on familiar
 *  ground). Review density is capped at ~22% and cross-strand at ~17%
 *  across all lengths so focal stays the dominant role.
 *
 *  Cases 3-8 are the original T1.4c lengths (kept: buildSessionRun is a
 *  pure function callable with any length, and the role unit tests pin
 *  3/5/7/8 directly). Cases 10/12/15/18/20 are the bumped age-band
 *  lengths (sessionLengthForAge). The all-focal `default` is now only a
 *  safety net for truly-unexpected lengths — every real age-band length
 *  has an explicit interleaved pattern, so longer sessions no longer
 *  silently lose spaced-review + cross-strand variety. */
function interleavePattern(sessionLength: number): SessionRunRole[] {
  const F = "focal" as const;
  const R = "spaced_review" as const;
  const X = "cross_strand" as const;
  switch (sessionLength) {
    case 3:
      return [F, F, F];
    case 4:
      // Light interleave: 1 review + 1 cross-strand, no double-focal in
      // a row except start/end.
      return [F, R, X, F];
    case 5:
      // Pre-T1.4c default; user's spec pattern.
      return [F, R, F, F, X];
    case 6:
      return [F, R, F, F, X, F];
    case 7:
      return [F, R, F, F, X, F, F];
    case 8:
      // Two reviews for the longest pre-bump session.
      return [F, R, F, F, X, F, R, F];
    case 9:
      // PR-AGE-MODEL: the recalibrated age 4.5-5.0 length (9). Without this
      // case it fell through to the all-focal `default`, silently stripping
      // spaced-review + cross-strand variety for that band (the same trap the
      // #75 length-bump fixed for 10+). 6F / 2R / 1X — interpolated between
      // case 8 (5F/2R/1X) and case 10 (7F/2R/1X).
      return [F, R, F, F, X, F, R, F, F];
    case 10:
      // 7F / 2R / 1X (R 20%, X 10%).
      return [F, R, F, F, X, F, R, F, F, F];
    case 12:
      // 8F / 2R / 2X (R 17%, X 17%).
      return [F, R, F, F, X, F, R, F, F, X, F, F];
    case 15:
      // 10F / 3R / 2X (R 20%, X 13%).
      return [F, R, F, F, X, F, R, F, F, X, F, R, F, F, F];
    case 18:
      // 11F / 4R / 3X (R 22%, X 17%).
      return [F, R, F, F, X, F, R, F, F, X, F, R, F, F, X, F, R, F];
    case 20:
      // 13F / 4R / 3X (R 20%, X 15%).
      return [F, R, F, F, X, F, R, F, F, X, F, R, F, F, X, F, R, F, F, F];
    default: {
      // Safety net for unexpected lengths: all focal.
      const out: SessionRunRole[] = [];
      for (let i = 0; i < sessionLength; i++) out.push(F);
      return out;
    }
  }
}

/** Numeric curriculum rank of a SKILL's authored age-band (ascending
 *  difficulty), resolved from the microSkills manifest via ageBandForSkill.
 *  -1 for a band not in AGE_BAND_ORDER (callers decide the fallback —
 *  pickReviewSkill substitutes the focal's rank; the MA-86 mastery lift
 *  never admits a negative rank because its explicit `skillAgeBandRank(id) >= 0`
 *  guard excludes it — `aboveBand.has` does NOT, since an unknown band ranks
 *  above every real band there). Hoisted (MA-86) from
 *  pickReviewSkill's inline `bandRankOf` so the mastery lift's net-currency
 *  cap and the review-proximity bias share ONE band-rank source. */
function skillAgeBandRank(skillId: string): number {
  return skillBandRankOrUnknown(ageBandForSkill(skillId));
}

/** MA-79 (ADR-106 Decision 4): the band → rank step of `skillAgeBandRank`,
 *  re-pointed onto the canonical `bandRank` (imported here as `ageBandRank`)
 *  with the unknown case handled EXPLICITLY: bandRank's unknown sentinel
 *  (AGE_BAND_ORDER.length) is mapped back to -1, so both call sites keep their
 *  exact semantics — the MA-86 net-currency cap
 *  `skillAgeBandRank(id) <= childRank + 1` and pickReviewSkill's `r < 0` focal
 *  fallback. (Without this an unknown band would rank above every real band:
 *  the cap would flip pass→fail and the fallback would never fire.) Exported
 *  only so the −1 branch can be executed with a synthetic unknown band
 *  (`test:ma79-band-vocabulary-pins`). Call-site semantics: the mastery lift now
 *  EXCLUDES unknown bands (`>= 0` guard); the bounded cap-lift keeps admitting
 *  -1 by contract (`test:ma772-skill-age-band-rank-call-site`). No real skill reaches it today
 *  (ageBandForSkill falls back to a canonical band). */
export function skillBandRankOrUnknown(band: string): number {
  const r = ageBandRank(band);
  return r === AGE_BAND_ORDER.length ? -1 : r;
}

/** MA-896 — the due tier's precedence rank: 0 = owes its MA-297 confirm
 *  (engine band secure/fluent and confirmedSecure !== true — exactly MA-298's
 *  due condition), 1 = due only for its tree delayed review (the bridge).
 *  Within a rank: oldest lastSeenAt first, skillKey tiebreak. */
function dueTierRank(s: SkillState): number {
  const owesConfirm =
    (s.masteryBand === "secure" || s.masteryBand === "fluent") && s.confirmedSecure !== true;
  return owesConfirm ? 0 : 1;
}

/** MA-298 + MA-896 — is this review candidate due for the review slot on
 *  `today` (a dayKey)? Due := its confirm or delayed review is REQUIRED and
 *  EARNABLE (`audienceForSkill === "independent_practice"`, the MA-297
 *  skillHasChapterCredit carve-out) AND it was last seen on an EARLIER local
 *  day AND it still owes one: the MA-297 cross-day confirm
 *  (`confirmedSecure !== true`, MA-298) or the tree's delayed review
 *  (`isTreeReviewPending`, MA-896 — the reviewCandidates bridge, so a skill
 *  whose confirm is in but whose delayed review has not passed stays due).
 *  The one due predicate: pickReviewSkill's due tier applies it to every
 *  candidate, and MA-917 applies it to the focal. Pure. */
function isDueForReviewSlot(s: SkillState, today: string): boolean {
  return (
    (s.confirmedSecure !== true || isTreeReviewPending(s)) &&
    // MA-297 skillHasChapterCredit carve-out (RED round 1 M1): only a
    // skill whose confirm is REQUIRED and EARNABLE can be due. A
    // Foundation-routed skill (audienceForSkill ≠ independent_practice)
    // is practised on FoundationSession, whose attempts are scaffolded
    // and never confirm — without this it would be due forever and take
    // over ReadingSession's review slot (ms.read.03 serves trace items
    // there with no answer tiles).
    audienceForSkill(s.skillKey) === "independent_practice" &&
    s.lastSeenAt != null &&
    dayKey(s.lastSeenAt) < today
  );
}

/** Pick the spaced-review skill: same strand as focal, secure-or-fluent,
 *  with the longest gap since last attempt. Excludes the focal itself.
 *
 *  RC-F zero-pool gate (§K fold A.2, LOCKED option b): when `poolSizeFor`
 *  is provided, candidates whose served item pool is empty are skipped —
 *  a review pick the content pipeline can't serve makes
 *  useEngineQuestionsForRun DROP the slot while sessionRun.questions
 *  keeps it, desyncing items[done] ↔ questions[done] (wrong skillId on
 *  every subsequent attempt + silent session shrink). Reading has several
 *  zero-pool routed skills (ms.read.02 is NOT one of them any more as of
 *  MA-671 slice 1b, 2026-09-19 — it now has a real 30-item pool), and math
 *  shares the latent flaw. When `poolSizeFor` is absent the function is
 *  total and behaves exactly as before (pure, no React imports).
 *
 *  MA-298 due-first tier (MA-DEC-0721 memo §4 edit 2): when `now` is a
 *  valid Date, a candidate that is DUE FOR ITS CROSS-DAY CONFIRM wins the
 *  slot before the longest-gap tier runs. Due := the candidate passes every
 *  filter below (so it is band secure/fluent — exactly MA-297's
 *  secureProvisional condition — same strand, not focal, renderable) AND
 *  `confirmedSecure !== true` AND its confirm is required + earnable
 *  (`audienceForSkill === "independent_practice"`, the exact carve-out
 *  skillHasChapterCredit applies) AND it was last seen on an EARLIER local
 *  calendar day than `now` (dayKey, the same day test deriveConfirmedSecure
 *  uses — so an independent correct served today can confirm it). Oldest-
 *  due first (lastSeenAt ascending, skillKey tiebreak). Zero due candidates,
 *  or `now` absent/invalid ⇒ the longest-gap code below runs unchanged, so
 *  the pick is byte-identical to the pre-MA-298 picker. `now` is threaded
 *  from the session screens (never read from the clock here).
 *
 *  MA-896 (KID-JOURNEY-2026-10-08 D-KJ-3): a skill the TREE shows secured
 *  whose delayed review has not passed (reviewCandidates.isTreeReviewPending,
 *  stamped by the aggregator from the tree's own projection) joins the due
 *  tier even when its engine band is below secure, and stays due after its
 *  MA-297 confirm — the review slot is the only place the screens stamp the
 *  isDelayedReview attempt the tree's "Mastered" needs. Order: MA-298's
 *  confirm-owed skills first (dueTierRank 0, unchanged), then tree-review-only
 *  skills (rank 1); oldest first within each. It never enters the longest-gap tier; with no such skill due the
 *  pick is byte-identical to the pre-MA-896 picker. Slot counts and the
 *  interleave pattern are untouched (buildSessionRun). */
function pickReviewSkill(
  focalSkillId: string,
  states: Record<string, SkillState>,
  poolSizeFor?: (skillId: string) => number,
  now?: Date,
): string | null {
  const focalStrand = strandForSkill(focalSkillId);
  // Same strand, not focal, seen, renderable — the filters every review
  // candidate passes (engine-band and MA-896 tree candidates alike).
  const reviewable = Object.values(states)
    .filter((s) => s.skillKey !== focalSkillId)
    .filter((s) => strandForSkill(s.skillKey) === focalStrand)
    .filter((s) => s.lastSeenAt != null)
    // RC-F: zero-pool candidates can't be rendered — skip them so the
    // NEXT-longest-gap candidate with content still gets reviewed
    // (instead of nulling review entirely when the top pick is dark).
    .filter((s) => (poolSizeFor ? poolSizeFor(s.skillKey) > 0 : true));
  const candidates = reviewable.filter(
    (s) => s.masteryBand === "secure" || s.masteryBand === "fluent",
  );
  // MA-896: a tree-secure skill whose delayed review has not passed (the
  // reviewCandidates bridge) is a review candidate even when its engine band
  // is below secure — but ONLY for the due tier below. The longest-gap tier
  // keeps the engine-band candidate set, so with no such skill (or none due)
  // the pick is byte-identical to the pre-MA-896 picker.
  const treeCandidates = reviewable.filter(
    (s) =>
      isTreeReviewPending(s) &&
      !(s.masteryBand === "secure" || s.masteryBand === "fluent"),
  );
  if (candidates.length === 0 && treeCandidates.length === 0) return null;

  // MA-298: the due-for-confirm tier (see JSDoc). Skipped entirely when no
  // valid `now` is threaded in, or when nothing is due. MA-896 widens it to
  // the tree candidates and to a tree-pending skill whose MA-297 confirm is
  // already in (isDueForReviewSlot); the order is unchanged.
  if (now instanceof Date && Number.isFinite(now.getTime())) {
    const today = dayKey(now.toISOString());
    const due = [...candidates, ...treeCandidates].filter((s) => isDueForReviewSlot(s, today));
    if (due.length > 0) {
      const seenMs = (iso: string | null): number => {
        const t = iso ? Date.parse(iso) : NaN;
        return Number.isNaN(t) ? 0 : t;
      };
      due.sort((a, b) => {
        // MA-896: MA-298's confirm-owed skills keep their precedence (they also
        // gate the chapter frontier, MA-297); tree-review-only skills follow.
        // With no tree-pending skill every due skill ranks 0 — the pre-MA-896 order.
        const r = dueTierRank(a) - dueTierRank(b);
        if (r !== 0) return r;
        const d = seenMs(a.lastSeenAt) - seenMs(b.lastSeenAt); // oldest first.
        return d !== 0 ? d : a.skillKey.localeCompare(b.skillKey);
      });
      return due[0].skillKey;
    }
  }
  if (candidates.length === 0) return null;

  // PR-#223 follow-up — BAND-PROXIMITY bias (gap-since-seen still PRIMARY).
  //
  // Pure "longest gap" reaches for the OLDEST secure skill, which tends to be
  // the easiest/most-distant one (lowest age-band). #223 clamped the served
  // item difficulty, but the *chosen skill* can still sit far below the focal
  // band. We add a gentle secondary bias toward a secure skill whose age-band
  // is NEAR (and ideally just below) the focal's, WITHOUT discarding the
  // spaced-retrieval intent: gap dominates, band-proximity only breaks
  // near-ties.
  //
  // Mechanism: score = gapMs - bandDistance * BAND_PROXIMITY_WEIGHT_MS, picked
  // descending. gapMs is each candidate's staleness measured from the most
  // recently-seen reference in the pool (>= 0; bigger = staler = more wanted).
  // bandDistance is how many curriculum age-bands separate the candidate from
  // the focal (0 = same band), so a closer band shaves a small, fixed amount
  // off the effective gap.
  //
  // WHY this preserves spaced-retrieval: BAND_PROXIMITY_WEIGHT_MS is only 12h,
  // and the band gap across the whole curriculum is <= 8 steps (~4 days max
  // penalty). A candidate whose gap is meaningfully longer (a day or more)
  // still wins outright — the bias can only re-order candidates whose gaps are
  // already within ~half a day of each other. So a much-staler skill is never
  // displaced by a band-nearer-but-fresh one; only genuine near-ties tilt
  // toward the focal band. Conservative + tunable (single constant below).
  const BAND_PROXIMITY_WEIGHT_MS = 12 * 60 * 60 * 1000; // 12h per band-step.

  // Numeric curriculum rank of a skill's age-band (ascending difficulty).
  // Unknown bands fall back to the focal's rank -> zero band-distance (no
  // bias either way), so an un-banded skill is ranked purely on gap as before.
  const focalBandRank = skillAgeBandRank(focalSkillId);
  const bandRankOf = (skillId: string): number => {
    const r = skillAgeBandRank(skillId);
    return r < 0 ? focalBandRank : r;
  };

  const epoch = (iso: string | null): number => {
    if (!iso) return 0;
    const t = Date.parse(iso);
    return Number.isNaN(t) ? 0 : t;
  };
  // Reference = the freshest lastSeenAt among candidates; every candidate's
  // gap is non-negative relative to it (the staleness ordering is identical
  // to the old oldest-first compare -- we just make it a magnitude so the
  // small band term can be measured against it).
  const refSeen = candidates.reduce(
    (max, c) => Math.max(max, epoch(c.lastSeenAt)),
    0,
  );
  const scoreOf = (c: SkillState): number => {
    const gapMs = refSeen - epoch(c.lastSeenAt); // >= 0, bigger = staler.
    const bandDistance =
      focalBandRank < 0 ? 0 : Math.abs(bandRankOf(c.skillKey) - focalBandRank);
    return gapMs - bandDistance * BAND_PROXIMITY_WEIGHT_MS;
  };
  candidates.sort((a, b) => {
    const d = scoreOf(b) - scoreOf(a); // descending score.
    if (d !== 0) return d;
    // Stable, deterministic tiebreak: older first (preserves prior intent),
    // then declaration-order proxy (lex on skillKey).
    const g = (a.lastSeenAt ?? "").localeCompare(b.lastSeenAt ?? "");
    return g !== 0 ? g : a.skillKey.localeCompare(b.skillKey);
  });
  return candidates[0].skillKey;
}

/** Pick the cross-strand easy-win: secure-or-fluent skill in the OTHER
 *  strand. Lowest-band tie → earliest declaration order (proxy: lex sort
 *  on skillId). Returns null when no candidate qualifies. */
function pickCrossStrandSkill(
  focalSkillId: string,
  states: Record<string, SkillState>,
): string | null {
  const focalStrand = strandForSkill(focalSkillId);
  if (focalStrand === "other") return null;
  const otherStrand = focalStrand === "math" ? "reading" : "math";
  const candidates = Object.values(states)
    .filter((s) => strandForSkill(s.skillKey) === otherStrand)
    .filter((s) => s.masteryBand === "secure" || s.masteryBand === "fluent");
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => a.skillKey.localeCompare(b.skillKey));
  return candidates[0].skillKey;
}

// ── #246 follow-up: assigned-skill cross-strand / review surfacing ──────
//
// PROBLEM this solves. PR #246 (select-to-assign) lets a parent queue
// skills for a child; useTodaysMission steers the queue HEAD as the FOCAL
// skill. But that surfaced an assigned skill in exactly ONE place — the
// focal pick. A parent who queues several skills (or whose queued skill
// isn't the current focal because an earlier queue entry still leads) saw
// the rest of the queue do nothing inside a given session run. The parent's
// intent — "make sure my kid MEETS these skills soon" — wants the assigned
// skills surfaced in the session's EXTRA slots too (spaced-review / cross-
// strand), not just the single focal stone.
//
// WHAT we do. When buildSessionRun is given the child's assigned queue, we
// preferentially fill the run's spaced_review and cross_strand slots with
// assigned-but-not-yet-met skills, AHEAD of the normal secure/fluent picks
// for those roles. This is deliberate: the normal review/cross-strand picks
// require a MASTERED (secure/fluent) skill, but an assigned-but-new skill is
// by definition NOT mastered — so without this carve-out an assigned skill
// could never reach these slots. The parent explicitly asked for it, so we
// let it in.
//
// CONSERVATIVE by design — the guardrails:
//   • POOL GATE. Every assigned candidate must have a non-empty served
//     content pool (poolSizeFor > 0), the SAME renderability gate the focal
//     and review picks use. We never route the kid into an item the content
//     pipeline can't render (e.g. the audio-gated zero-pool reading skills).
//     When poolSizeFor is absent (pure/ungated callers + unit tests) the
//     gate no-ops, matching pickReviewSkill's totality contract.
//   • DROP-WHEN-MET. We reuse assignedSkills.ts's prune predicates: an
//     assigned skill the kid has already been INTRODUCED to (>=1 attempt) or
//     has MASTERED is filtered out — it no longer needs surfacing, exactly
//     mirroring the focal-pick drop rule. One source of truth for "done".
//   • DE-DUPE vs FOCAL + each other. The focal skill is never re-surfaced as
//     an extra slot (it already dominates the run), and each assigned skill
//     fills at most one extra slot per session.
//   • CAP. At most MAX_ASSIGNED_EXTRA_SLOTS assigned skills enter the extra
//     slots in a single session, so a long queue can't crowd out the normal
//     review/cross-strand variety wholesale. The cap is intentionally small:
//     assigned skills are a NUDGE layered on top of adaptive selection, not a
//     replacement for it.
//   • PRECEDENCE. Within the capped budget, assigned skills WIN the extra
//     slots over the normal secure/fluent review + cross-strand picks (the
//     parent's explicit intent outranks the engine's automatic variety
//     choice). Any extra slots left after the assigned budget is spent fall
//     back to the normal picks, unchanged.
//   • ORDER PRESERVED. Assigned skills are consumed queue-head-first (FIFO),
//     matching useTodaysMission's focal ordering, so the parent's tap order
//     is honored consistently across focal + extra slots.
//
// PURE. No React, no storage — the queue + states + pool lookup are all
// injected, so this is unit-testable in isolation alongside pickReviewSkill.

/** Max assigned skills that may occupy a single run's extra (review +
 *  cross-strand) slots. Small on purpose — assigned skills NUDGE the run;
 *  they don't take it over. A run typically has 1-2 review + 1-2 cross-
 *  strand slots, so 2 covers the common "fill both extra roles" case while
 *  still leaving room for normal variety in longer sessions. */
const MAX_ASSIGNED_EXTRA_SLOTS = 2;

/** Whether an assigned skill QUALIFIES to be surfaced in an extra slot:
 *  not the focal, not already met (introduced/mastered — assignedSkills.ts
 *  prune predicates), and renderable (non-empty served pool; gate no-ops
 *  when poolSizeFor absent, matching pickReviewSkill). Pure + null-safe. */
function assignedSkillEligible(
  id: string,
  focalSkillId: string,
  states: Record<string, SkillState>,
  poolSizeFor?: (skillId: string) => number,
): boolean {
  if (!id || id === focalSkillId) return false;
  // Reuse the focal-pick drop rule: already-met skills don't need surfacing.
  // (isSkillIntroduced/isSkillMastered are null-safe on an absent state ->
  // treated as not-met, so a brand-new assigned skill the kid has never
  // touched correctly qualifies.)
  if (isSkillIntroduced(id, states) || isSkillMastered(id, states)) return false;
  // Renderability gate — never surface a skill the pipeline can't serve.
  if (poolSizeFor && poolSizeFor(id) <= 0) return false;
  return true;
}

/** Pick the ordered list of assigned skills to surface in a run's EXTRA
 *  (spaced_review / cross_strand) slots, queue head->tail, capped at
 *  MAX_ASSIGNED_EXTRA_SLOTS, de-duped (first occurrence wins; focal
 *  excluded), each entry assignedSkillEligible (not-met + renderable).
 *
 *  STRAND-AWARENESS (important for single-strand screens). The run's
 *  spaced_review slot is SAME-STRAND-as-focal and cross_strand is the
 *  OTHER strand; the session screens render content per the focal strand
 *  and (MathSession/ReadingSession) substitute cross_strand back to focal so
 *  they only ever render their own strand. If we dropped an OTHER-strand
 *  assigned skill into a SAME-strand review slot, the single-strand screen
 *  would try to render the wrong strand's content for that slot. So we sort
 *  eligible assigned skills into two buckets by strand-vs-focal and let the
 *  builder draw from the matching bucket per role: same-strand assigned
 *  skills feed review slots, other-strand assigned skills feed cross_strand
 *  slots. The OVERALL cap still applies across both buckets combined.
 *
 *  Returns { sameStrand, otherStrand } id lists (order preserved). Both
 *  empty -> caller behaves exactly as pre-#246-follow-up. Pure. */
export function pickAssignedExtraSkills(
  assignedSkillIds: readonly string[],
  focalSkillId: string,
  states: Record<string, SkillState>,
  poolSizeFor?: (skillId: string) => number,
): { sameStrand: string[]; otherStrand: string[] } {
  const focalStrand = strandForSkill(focalSkillId);
  const sameStrand: string[] = [];
  const otherStrand: string[] = [];
  const seen = new Set<string>([focalSkillId]);
  let total = 0;
  for (const id of assignedSkillIds) {
    if (total >= MAX_ASSIGNED_EXTRA_SLOTS) break;
    if (!id || seen.has(id)) continue;
    seen.add(id);
    if (!assignedSkillEligible(id, focalSkillId, states, poolSizeFor)) continue;
    if (strandForSkill(id) === focalStrand) sameStrand.push(id);
    else otherStrand.push(id);
    total += 1;
  }
  return { sameStrand, otherStrand };
}

/** Build the session's question manifest.
 *
 *  Research § Daily-set composition: every session interleaves focal,
 *  one spaced-review, and one cross-strand easy-win. The kid doesn't
 *  grind one skill for the entire session — variety supports retention
 *  and motivation.
 *
 *  Selection:
 *    - focal: passed in (from buildTodaysMission)
 *    - spaced_review: secure-or-fluent skill in focal's strand with
 *      the LONGEST gap since last attempt — except that, when `now` is
 *      passed, a skill DUE for its MA-297 cross-day confirm wins first
 *      (MA-298, see pickReviewSkill)
 *    - cross_strand: secure-or-fluent skill in the OTHER strand,
 *      earliest in declaration order on ties
 *
 *  Substitution: when a role's pick is null (no candidate qualifies),
 *  that slot falls back to focal and `substitutionsCount` increments.
 *  Surfaces in parent-results as "no variety today — still building
 *  review base."
 *
 *  Architectural constraint (T1.4b): cross-strand attempts still write
 *  with sessionSource="path" — they are path work, just in the other
 *  strand. T1.4b's filterPathAttempts admits them, so they correctly
 *  advance the cross-strand skill's mastery and the chapter containing
 *  that skill — independently of the focal chapter.
 *
 *  RC-F: `poolSizeFor` (optional) gates the review pick on a non-zero
 *  served item pool — see pickReviewSkill. Session screens pass a count
 *  over the SAME pool path their review pull uses: MathSession
 *  servedItemCountForSkill (useEngineQuestions.ts); ReadingSession
 *  readingReviewPoolSize (readingReviewGate.ts, MA-768 — only items the
 *  reading screen can render, the same filter its review pull applies).
 *  Absent → prior (ungated) behavior;
 *  the function stays pure with no React imports either way.
 */
export function buildSessionRun(
  focalSkillId: string,
  skillStates: Record<string, SkillState>,
  sessionLength: number,
  poolSizeFor?: (skillId: string) => number,
  // #246 follow-up: the child's assigned (parent-queued) skill ids, queue
  // head->tail. Optional + defaults to [] so EVERY existing caller is
  // byte-for-byte unchanged in behavior (no assigned skills -> empty extras
  // -> the exact prior review/cross-strand logic runs). See
  // pickAssignedExtraSkills for the surfacing rules + guardrails.
  assignedSkillIds: readonly string[] = [],
  // MA-298: the session's wall clock, threaded to pickReviewSkill's
  // due-for-confirm tier. Session screens MUST pass a real `new Date()`
  // (read where the run is built); absent ⇒ the clock-free longest-gap
  // pick, byte-identical to the pre-MA-298 behavior (pure unit callers).
  now?: Date,
): SessionRun {
  const reviewSkillId = pickReviewSkill(focalSkillId, skillStates, poolSizeFor, now);
  const crossStrandSkillId = pickCrossStrandSkill(focalSkillId, skillStates);
  const pattern = interleavePattern(sessionLength);

  // #246 follow-up: resolve the (capped, gated, de-duped, strand-bucketed)
  // assigned skills to surface in the extra slots. SAME-strand assigned
  // skills feed spaced_review slots; OTHER-strand ones feed cross_strand
  // slots — matching each role's existing strand semantics so a single-
  // strand screen never renders the wrong strand for a slot. Each bucket is
  // consumed FIFO; assigned skills take PRECEDENCE over the normal secure/
  // fluent pick for that role. Both empty -> behaves exactly as before.
  const { sameStrand: assignedReview, otherStrand: assignedCross } =
    pickAssignedExtraSkills(
      assignedSkillIds,
      focalSkillId,
      skillStates,
      poolSizeFor,
    );
  let reviewCursor = 0;
  let crossCursor = 0;
  const assignedSurfacedSkillIds: string[] = [];

  let substitutionsCount = 0;
  const questions: SessionRunQuestion[] = pattern.map((role, ordinalInRun) => {
    let skillId: string;
    let actualRole: SessionRunRole = role;
    if (role === "spaced_review") {
      // PRECEDENCE: a same-strand assigned skill claims this extra slot
      // first (parent intent > automatic variety). The role stays
      // "spaced_review" so the run shape + interleave pattern are untouched;
      // only the skillId changes. Falls back to the normal review pick, then
      // to focal substitution.
      if (reviewCursor < assignedReview.length) {
        skillId = assignedReview[reviewCursor];
        reviewCursor += 1;
        assignedSurfacedSkillIds.push(skillId);
      } else if (reviewSkillId) {
        skillId = reviewSkillId;
      } else {
        skillId = focalSkillId;
        actualRole = "focal";
        substitutionsCount += 1;
      }
    } else if (role === "cross_strand") {
      if (crossCursor < assignedCross.length) {
        skillId = assignedCross[crossCursor];
        crossCursor += 1;
        assignedSurfacedSkillIds.push(skillId);
      } else if (crossStrandSkillId) {
        skillId = crossStrandSkillId;
      } else {
        skillId = focalSkillId;
        actualRole = "focal";
        substitutionsCount += 1;
      }
    } else {
      skillId = focalSkillId;
    }
    return { skillId, role: actualRole, ordinalInRun };
  });
  return {
    questions,
    focalSkillId,
    reviewSkillId,
    crossStrandSkillId,
    substitutionsCount,
    assignedSurfacedSkillIds,
  };
}

// ── T1.4c: age-scaled session length ──────────────────────────────────

/** Items per strand by age band, per session-design research lines 65-68
 *  ("Recommended PencilWise model" table). The daily trail is 3 strands,
 *  so daily volume ≈ 3 × this (e.g. age 6 → 3 × 15 = ~45 items/day, the
 *  locked INDEX.md daily-volume target).
 *
 *  RESEARCH-BACKED bands (session-design research lines 65-68):
 *    4.0-4.5 → 8    (line 65: Math 2×4, Reading 2×4, Mixed 2×4)
 *    5.0-5.5 → 12   (line 66: Math 3×4, Reading 3×4; Mixed differs — 2×5=10
 *                    — see blockSizeForAge note; v1 treats strands uniformly)
 *    6.0-6.5 → 15   (line 67: Math 3×5, Reading 3×5, Mixed 3×5)
 *    6.5-7.0 → 18   (line 68: Math 3×6, Reading 3×6, Mixed 3×6)
 *
 *  INTERPOLATED half-bands (calibration targets, NO direct research line —
 *  the research table cites whole/half years 4,5,6,7; these fill the gaps):
 *    4.5-5.0 → 9    (between age-4's 8 and age-5's 12)
 *    5.5-6.0 → 12   (age-5/6 border; conservative low end of the 12-15 span)
 *    7.0+    → 20   (research tops out at age 7 = 18; +2 extrapolation)
 *
 *  CHANGE vs prior (T1.4c Kumon-feedback calibration → this research-aligned
 *  pass, PR-AGE-MODEL):
 *    4.0-4.5:  5 → 8   (+3, was undercounting research's 2×4)
 *    4.5-5.0:  7 → 9   (+2, interpolated)
 *    5.0-5.5: 10 → 12  (+2, was undercounting research's 3×4)
 *    5.5-6.0: 12 → 12  (no change)
 *    6.0-6.5: 15 → 15  (no change — already research-aligned)
 *    6.5-7.0: 18 → 18  (no change)
 *    7.0+:    20 → 20  (no change)
 *
 *  NOTE (PR-AGE-MODEL Sasha trace): the SERVED length a screen renders is
 *  `Math.min(sessionLengthForAge(band), pool.length)` — an under-resourced
 *  focal skill caps the pool at uniques×2 via withBoundedRepetition (#27),
 *  so this value is the CONFIGURED ceiling, not a guarantee. Content-pool
 *  starvation (the real cause of short age-6 sessions) is a Phase-H content
 *  authoring concern, NOT an age-length one. Parent settings surfaces both
 *  the configured AND the effective served length so the gap is visible.
 *
 *  FOLLOW-UPS (NOT INCLUDED — genuine deferrals):
 *    - 7.0+ "keep going" extension (deferred per T1.4c NOT INCLUDED list).
 *    - Mid-session save state (P-SESSION-SAVE-STATE): if kids fatigue at
 *      12+ items in live-test, resumable sessions become urgent. Not
 *      built preemptively — flagged as the observation point.
 */
export function sessionLengthForAge(ageBand: AgeBand): number {
  // Phase 0.1 — read the ordered band table (was a per-band switch). The
  // legacy per-band rationale now lives as comments in ageBandTable.ts.
  return ageBandRow(ageBand).sessionLength;
}

// ── PR-AGE-MODEL: age-scaled chunk (block) size ───────────────────────

/** Block size for the in-session chunk checkpoints, given the kid's age
 *  band, per session-design research line 38 ("blocks of 5 items for
 *  recognition-heavy work") read against the per-band table (lines 65-68):
 *   - Ages 4–5.5: blocks of 4 (recognition-heavy work, younger attention —
 *     research's age-4/5 columns use ×4 groupings)
 *   - Ages 5.5–6.5: blocks of 5 (research line 38 directly; the age-6
 *     column is 3×5)
 *   - Ages 6.5+: blocks of 6 (the age-7 column is 3×6)
 *
 *  v1 SIMPLIFICATION: ONE block size per band, assuming uniform
 *  recognition-heavy work across all strands. Research's table at lines
 *  65-68 actually distinguishes:
 *   - Production-heavy work (writing/tracing): smaller blocks (2-3 for
 *     younger, 3-4 for age 6, 4-5 for age 7).
 *   - Age 5 specifically: the Mixed strand is 2×5 (10 items, blocks of 5),
 *     while Math/Reading are 3×4 (12 items, blocks of 4).
 *  PencilWise does not model production-heavy mode or Mixed-specific
 *  chunking explicitly today. If later content adds those distinctions,
 *  this helper can take optional `mode` and/or `strand` parameters.
 *
 *  Labeled calibration target — a design inference from the research
 *  groupings, not a settled formula. Tune if live signals warrant.
 *
 *  Lives here (engine), NOT in ChunkCheckpoint.tsx, so session screens
 *  import + call one source of truth; the old `BLOCK_SIZE = 5` constant in
 *  ChunkCheckpoint is removed by PR-AGE-MODEL.
 */
export function blockSizeForAge(ageBand: AgeBand): number {
  // Phase 0.1 — read the ordered band table (was a per-band switch).
  return ageBandRow(ageBand).blockSize;
}

// MA-343 (ADR-099): `previewWindowForAge` — the age-scaled preview-window
// clip that fed the retired PATH_SPEC demo layer (`buildStorybookPath`,
// `useStorybookPath`) — is DELETED. The Journey tab replaces it with a
// chapter-scoped bound (`buildJourneyView`, src/lib/engine/journeyView.ts):
// chapters strictly before "current" collapse to a banner, "current"
// renders full stone lanes, "current + 1" is a teaser, and anything
// further out is simply absent. See ADR-099 for the full rationale; its
// pins (progression.test.mjs T21-T27, the relevant ageBandTable.test.mjs
// assertions) are removed in the same diff. The `previewWindow` band-table
// field and the `PREVIEW_WINDOW_FULL_CHAPTER` sentinel are deleted too —
// this was their only consumer (verified via a repo-wide grep).

// ── T1.4b: buildTodaysMission (engine focal-stone selection) ──────────

/** Lightweight chapter projection used by buildTodaysMission. Drops
 *  the celebrationPayload + parent-facing fields the engine doesn't
 *  care about; mirrors what the chapters.json manifest exposes. */
interface ChapterForMission {
  id: string;
  milestoneSkills: string[];
  supportSkills: string[];
}

/** Output of buildTodaysMission — the focal stone the kid should tap
 *  today. Stays as a skill id (not a Stone object) so callers can
 *  decide how to map to UI. */
interface TodaysMissionPick {
  /** The focal skill id (e.g. "ms.read.06"). null when no chapter
   *  has any unlocked skill — the absolute-fresh-no-progress case
   *  the caller handles by falling back to recommendStartingSkill. */
  focalSkillId: string | null;
  /** The chapter the focal skill belongs to. null when focalSkillId
   *  is null. */
  chapterId: string | null;
  /** Diagnostic — what selection rule fired (useful for the e2e
   *  asserting "today-stone derivation"). */
  rationale:
    | "lowest_band_in_current_chapter"   // (a) standard mid-chapter pick
    | "first_stone_of_next_chapter"      // (b) cross-chapter transition
    | "most_recent_in_progress"          // (c) fallback when no chapter qualifies
    | "runway_unconfirmed_after_chapters_complete" // MA-839: every chapter complete, an in-cap skill is unconfirmed/unstarted
    | "review_rotation_end_of_runway" // MA-849: every in-cap skill confirmed (end of the runway): the oldest-seen one is reviewed
    | "no_progression_state";            // empty SkillState map — caller falls back
}

/** Order skills within a chapter by mastery band, lowest first. Skills
 *  not in the SkillState map count as "new" (worst band). */
function bandRank(band: MasteryBand): number {
  switch (band) {
    case "new":      return 0;
    case "emerging": return 1;
    case "secure":   return 2;
    case "fluent":   return 3;
  }
}

/** Narrow a Chapter | ChapterForMission down to the full Chapter shape
 *  chapterComplete needs. ChapterForMission is a lightweight projection
 *  (id + milestoneSkills + supportSkills only); the synthesized fields
 *  below are never read by chapterComplete (gate-stone handling is a
 *  T1.4b stub — see the T1.4a comment there), so this is a safe,
 *  behavior-preserving widening. */
function toChapterShape(ch: Chapter | ChapterForMission): Chapter {
  if ("chapterGateStoneId" in ch) return ch as Chapter;
  return {
    id: ch.id,
    canonicalSlug: ch.id,
    titleKid: "",
    titleParent: "",
    milestoneSkills: ch.milestoneSkills,
    supportSkills: ch.supportSkills,
    chapterGateStoneId: "",
    celebrationPayload: { badge: "", printableTitle: "", parentSharePrompt: "" },
  };
}

/** A chapter is "unlocked" when its predecessor chapter is complete
 *  (per chapterComplete), OR when it's the first chapter. Order is
 *  the chapters[] array order — chapters.json declares the canonical
 *  sequence. */
function isChapterUnlocked(
  chapter: Chapter | ChapterForMission,
  chapters: (Chapter | ChapterForMission)[],
  states: Record<string, SkillState>,
  // RC-R2 (D-RC-4): forwarded into chapterComplete so a zero-pool
  // milestone can't deadlock the unlock chain.
  poolSizeFor?: (skillId: string) => number,
  // MA-88: age-mastery predicate forwarded to chapterComplete's vacuous-
  // completion guard. Absent → guard is a no-op (backward compat).
  isMasterySkipped?: (skillId: string) => boolean,
  // MA-246 (ADR-068): the child's already-credited chapter ids (persisted
  // chapter_complete events). Forwarded into chapterComplete's
  // support-coverage ratchet for the PREVIOUS chapter. Absent/null →
  // no-op (back-compat).
  completedChapterIds?: ReadonlySet<string> | null,
): boolean {
  const idx = chapters.findIndex((c) => c.id === chapter.id);
  if (idx <= 0) return true;
  const prev = chapters[idx - 1];
  return chapterComplete(
    toChapterShape(prev),
    states,
    poolSizeFor,
    isMasterySkipped,
    completedChapterIds?.has(prev.id) === true,
  );
}

/** Pick the focal stone for today.
 *
 *  Research § Architecture spec ("buildTodaysMission" intent): given the
 *  child's chapter progress and SkillState, surface the single stone
 *  the engine wants front-and-center.
 *
 *  Selection order:
 *    (a) lowest-mastery (lowest band, earliest in declaration order on
 *        ties) skill in the FIRST unlocked, not-yet-complete chapter,
 *        among that chapter's milestones + support skills
 *    (b) if all chapters are complete: the first milestone of the LAST
 *        chapter (showcase / re-entry stone — the kid keeps revisiting
 *        the highest content rather than the empty "no chapter
 *        qualifies" state)
 *    (c) fallback: the skill with the most-recent lastSeenAt across
 *        ALL chapters — the "you were here last time" return point
 *    (d) absolute-fresh state (no SkillStates at all) → return
 *        focalSkillId=null with rationale="no_progression_state" so
 *        the caller falls back to recommendStartingSkill.
 *
 *  RC-R zero-pool focal gate (§K fold B.1): when the optional
 *  `poolSizeFor` is provided, the ranked pick (a) skips skills whose
 *  served item pool is empty — a zero-pool focal sends the kid into a
 *  session that can render NOTHING (post-RC-F: a permanent "Loading…").
 *  (ms.read.02 was this section's standing example until MA-671 slice 1b,
 *  2026-09-19 gave it a real 30-item pool; it is no longer zero-pool and
 *  no longer skipped here.) The next-ranked skill
 *  with content wins instead. If EVERY candidate in the chapter is
 *  zero-pool, the ungated ranked pick stands (the function stays total).
 *  RC-R2: `poolSizeFor` is ALSO forwarded into the chapter-completion
 *  predicate (isChapterUnlocked → chapterComplete), resolving the
 *  D-RC-4 milestone deadlock — a zero-pool milestone no longer blocks
 *  chapter advancement (see chapterComplete's JSDoc for the contract).
 *  Absent `poolSizeFor` → prior behavior exactly. Callers pass
 *  servedItemCountForSkill (useEngineQuestions.ts) — the SAME pool path
 *  a real session renders — mirroring RC-F's buildSessionRun gate. Pure:
 *  no React imports either way. */
/** AGE-GATE companion: return a copy of `states` with a synthetic FLUENT
 *  SkillState for each age-implied-mastered skill that has no real state yet,
 *  so buildTodaysMission's chapter walk treats aged-out early skills as
 *  complete and routes the child to age-appropriate content. Real evidence
 *  always wins — a skill the child has actually practiced is never
 *  overwritten/downgraded. Pure; no I/O. */
export function withAgeImpliedMastery(
  states: Record<string, SkillState>,
  masteredSkillIds: readonly string[],
): Record<string, SkillState> {
  if (masteredSkillIds.length === 0) return states;
  const out: Record<string, SkillState> = { ...states };
  for (const id of masteredSkillIds) {
    // Age dominates for the PICK: a 7yo has aged out of letter-naming even
    // if they tapped a few items, so force the masked skill to fluent
    // regardless of any real state (forcing fluent only ever UPGRADES — it
    // never downgrades a genuinely-mastered skill). This is PICK-ONLY and
    // EPHEMERAL: the returned map is passed straight to buildTodaysMission;
    // it is never persisted and never reaches the parent dashboard / real
    // SkillState aggregation, so it cannot fabricate progress anywhere else.
    out[id] = {
      skillKey: id,
      attemptsTotal: 0,
      correctTotal: 0,
      sessionsSeen: 0,
      lastSeenAt: null,
      lastPassedAt: null,
      consecutiveFails: 0,
      consecutivePasses: 0,
      supportLevel: "none",
      masteryBand: "fluent",
      retentionConfidence: 1,
      // MA-297: age-implied mastery is DEEMED credit (the child aged past
      // this skill; AGE_MASTERY_GAP=4 bands below) — it must satisfy the
      // chapter gate without ever being served, or the gate would stall an
      // older child on un-servable early chapters (the age-fit false-PASS
      // class MA-88/ADR-034 closed). Synthetic + ephemeral, PICK-ONLY,
      // exactly like the fluent band it accompanies.
      confirmedSecure: true,
    };
  }
  return out;
}

// PR-E (placement → engine SOFT re-rank). Kill-flag: flip to false to disable
// the placement-floor reorder on the core ranker WITHOUT a revert (the param
// stays threaded; the key just goes uniformly 0 = no-op).
const PLACEMENT_FLOOR_ENABLED = true;

type PlacementFloorSet = {
  reading: PlacementFloor | null;
  math: PlacementFloor | null;
};

/** Demotion safety valve: a child mis-placed TOO HIGH must have a path DOWN.
 *  When the floor-level skill shows a sustained plateau (3+ consecutive fails)
 *  or sustained low accuracy (<50% over >=4 attempts), the placement floor for
 *  that strand is LOWERED (disabled) so below-floor (easier) content is no
 *  longer deprioritised. updatePacingFlag already detects the plateau; nothing
 *  acted on it before. */
export function placementValveLowered(
  floor: PlacementFloor,
  states: Record<string, SkillState>,
): boolean {
  const st = states[floor.skillId];
  if (!st) return false;
  if (updatePacingFlag(st) === "plateau") return true;
  if (st.attemptsTotal >= 4 && st.correctTotal / st.attemptsTotal < 0.5) return true;
  return false;
}

/** Placement re-rank key for one candidate: 1 = SOFT-deprioritised (a NEW
 *  below-placement-floor skill of the placed strand), 0 = preferred.
 *  Precedence skillStates > placement > age:
 *   - no placement / kill-flag off / wrong strand → 0 (no-op);
 *   - demotion valve active for the strand → 0 (path down for an over-placed kid);
 *   - the child has DEMONSTRATED practice (any attempts) on this skill → 0
 *     (engagement wins; placement never yanks a kid off work they're doing) —
 *     MA-851: unless the skill is FINISHED (chapter credit) and nothing in the
 *     ranked set is still open at/above the floor (`isFinished`, see rankCandidates);
 *   - otherwise 1 iff the skill is below the strand's placement floor. */
function placementRerankKey(
  skillId: string,
  floors: PlacementFloorSet | null | undefined,
  states: Record<string, SkillState>,
  // MA-851: given only when the ranked set holds NO open preferred candidate
  // (see rankCandidates). A played skill that is finished (chapter credit) then
  // loses the engagement exemption below.
  isFinished?: (skillId: string) => boolean,
): 0 | 1 {
  if (!PLACEMENT_FLOOR_ENABLED || !floors) return 0;
  const strand = strandForSkill(skillId);
  if (strand !== "math" && strand !== "reading") return 0;
  const floor = floors[strand];
  if (!floor) return 0;
  if (placementValveLowered(floor, states)) return 0;
  if ((states[skillId]?.attemptsTotal ?? 0) > 0 && !isFinished?.(skillId)) return 0;
  return isBelowFloor(skillId, floor) ? 1 : 0;
}

/**
 * MA-775 — the skill ids the parent SkillTree shows as `locked` ("Ahead"):
 * the SAME per-strand fold SkillTree.tsx runs (sortStrandByAgeBand →
 * computeStrandStatuses over the tree's age-cover, above-band and suppressed
 * sets), over the tree's own nodes (useSkillGraph.buildSkillGraphNodes).
 * Threaded into buildTodaysMission so the focal ranker prefers a skill the
 * tree does not badge "Ahead" — in practice the strand's frontier. Pure.
 */
export function treeLockedSkillIds(
  nodes: readonly (SkillStatusNode & { strand: string; ageBand: string })[],
  ageCovered: Iterable<string>,
  aboveBand: Iterable<string>,
  suppressed: Iterable<string>,
): Set<string> {
  const covered = new Set(ageCovered);
  const above = new Set(aboveBand);
  const removed = new Set(suppressed);
  const locked = new Set<string>();
  for (const strand of new Set(nodes.map((n) => n.strand))) {
    const sorted = sortStrandByAgeBand(nodes.filter((n) => n.strand === strand));
    // MA-85: rule (b) (a secured skill's prerequisites read "Not yet") is
    // tree-display only in that slice; this serve-ranker input keeps its
    // pre-MA-85 locked set so the mission pick does not move.
    for (const { node, status } of computeStrandStatuses(sorted, covered, above, removed, { notYetForSecuredPrereqs: false })) {
      if (status === "locked") locked.add(node.id);
    }
  }
  return locked;
}

/** MA-88's age-mastery predicate, factored out so useTodaysMission (MA-246)
 *  can derive the SAME predicate buildTodaysMission uses internally when it
 *  independently probes chapterComplete for newly-crossed chapters — single
 *  source of truth, no duplicated derivation. Pure; no I/O. Absent/invalid
 *  selectionAge → undefined (the guard no-ops, exactly as before). */
export function masterySkippedPredicate(
  selectionAge?: number | null,
): ((skillId: string) => boolean) | undefined {
  if (selectionAge == null || !Number.isFinite(selectionAge)) return undefined;
  const masteredIds = new Set(ageImpliedMasteredSkillIds(selectionAge));
  return (id: string) => masteredIds.has(id);
}

export function buildTodaysMissionPick(
  chapters: (Chapter | ChapterForMission)[],
  states: Record<string, SkillState>,
  poolSizeFor?: (skillId: string) => number,
  // AGE PLACEMENT FLOOR (Wave2-C): the resolved SELECTION AGE (years). When
  // supplied, the focal ranker prefers candidates at/above the age floor
  // (within AGE_FLOOR_GAP bands of the child) over far-below-age ones, so an
  // older child is not handed a much-too-easy focal skill the mastery-band
  // walk would otherwise rank first. Age is threaded in (NOT re-derived) so it
  // matches the SAME resolveSelectionAge value the age-skip gate already uses
  // upstream (useTodaysMission); a null/omitted age makes the floor a no-op,
  // so absent it the function behaves exactly as before. The floor is a SOFT
  // RE-RANK among already-reachable/unlocked candidates only: it never unlocks
  // a chapter early, never skips an unmet prerequisite chain, and degrades to
  // current behaviour when no at/above-floor candidate is reachable (see the
  // ranker below + ageFloorBelowSkillIds JSDoc).
  selectionAge?: number | null,
  // PR-E: per-strand placement floors (reading/math), precomputed by the
  // caller from the COMPLETED placement (placementFloor util) — threaded, not
  // re-derived, exactly like selectionAge. When supplied, below-placement-floor
  // NEW skills of the placed strand are SOFT-deprioritised in the focal ranker
  // (never removed; demonstrated practice + the plateau demotion valve override
  // it). Absent/null → byte-identical to before.
  placementFloors?: PlacementFloorSet | null,
  // MA-89 (ADR-052): the per-child suppressedSkillIds set (parent-removed
  // served "Now"s). A HARD MEMBERSHIP exclusion — unlike the soft `floor`
  // ranking key, a suppressed id never becomes the focal pick, in EITHER the
  // on/below-band eligible set or the above-band cap-lift set (MA-86's
  // ceiling), and never via the lastSeenAt/first-stone fallback chain below
  // (closing the "fully-suppressed chapter" gap: without also filtering the
  // fallback, suppressing every candidate in the target chapter would fall
  // through to the UNGATED global fallback and could re-serve a suppressed
  // id, or one that violates the band floor/ceiling). Does NOT apply to the
  // assigned-skill queue (useTodaysMission) — that path is upstream, additive
  // parent intent, and already bypasses the MA-86 ceiling by design; ADR-052
  // scopes suppression to the engine's OWN pick, not a deliberate assignment.
  // Absent/empty → byte-identical to before.
  suppressedSkillIds?: Iterable<string> | null,
  // MA-193: an OPTIONAL, audience-aware pool check used ONLY to decide
  // whether a chapter's SUPPORT skill is a rankable focal candidate — e.g.
  // servedItemCountForSkill(sid, len, audienceForSkill(sid)), which returns
  // a nonzero "scaffolded" pool for FOUNDATION_SKILL_IDS skills (ms.read.04/
  // 07) whose independent_practice pool is permanently 0 (ADR-055). Applied
  // ONLY inside the two ranking `.find()` calls below, and ONLY when the
  // candidate id is a member of `ch.supportSkills` — it NEVER reaches
  // `isChapterUnlocked`/`chapterComplete` (both still receive the bare
  // `poolSizeFor` untouched), so a scaffolded-only MILESTONE skill (e.g.
  // ms.read.01, ch1) can NEVER start gating chapter completion through this
  // param — see ADR-057. Absent → byte-identical to before (a support
  // skill whose only servable audience is "scaffolded" stays organically
  // unreachable, same as today; still reachable via parent-Assign, which
  // already threads audienceForSkill separately).
  supportServePoolSizeFor?: (skillId: string) => number,
  // MA-246 (ADR-068): the child's already-credited chapter ids (persisted
  // chapter_complete events) — see chapterComplete's supportCoverageCredited
  // JSDoc. Threaded (not re-derived) so useTodaysMission can fetch it once
  // alongside the caller's other milestone-event reads. Absent/null →
  // byte-identical to before.
  completedChapterIds?: ReadonlySet<string> | null,
  // MA-299 (MA-DEC-0721 #3): skill ids newly introduced TODAY (zero
  // attempts before today), threaded — not re-derived — from
  // useTodaysMission via newlyIntroducedSkillIdsToday(attempts). Counted
  // PER STRAND (math/reading) below; once a strand's count reaches
  // NEW_SKILL_DAILY_CAP, the ranker soft-deprioritises further zero-attempt
  // candidates OF THAT STRAND below every attempted candidate (see
  // rankCandidates' newIntroCap key). A MEMBERSHIP gate this is not — an
  // all-new chapter still serves (band/idx tiebreak still resolves a pick).
  // Absent/null/empty → byte-identical to before (existing "new"-first band
  // preference stands, exactly as pre-MA-299).
  introducedTodaySkillIds?: ReadonlySet<string> | null,
  // MA-775: the ids the parent SkillTree shows as `locked` ("Ahead") for
  // this child (treeLockedSkillIds). A SOFT ranking key directly under the
  // placement key: an unlocked candidate (the strand's tree frontier, a
  // practicing skill) ranks ahead of a tree-locked one, so the served focal
  // skill is one the tree does not badge "Ahead" whenever the chapter has
  // one. Never removes a candidate. Absent/null → byte-identical to before.
  treeLockedIds?: ReadonlySet<string> | null,
  // MA-851 (rule B only): the demotion valve as the /play/foundation guard and the Journey tap read it
  // (floorValve.floorValveLoweredForChild: THIS device's local attempt mirror), so Today names what the CTA
  // lands on even when the merged cloud+local states the ranker reads disagree (a fresh or second device).
  // The ranker itself keeps the merged states. Absent -> derived from `states` (single-source callers, tests).
  guardValveLowered?: boolean,
): TodaysMissionPick {
  const suppressed = new Set(suppressedSkillIds ?? []);
  // MA-299: per-strand count of today's already-introduced new skills,
  // computed once (independent of the chapter loop, like floorActive/
  // aboveBand above).
  const newIntroCountByStrand: Record<"math" | "reading" | "other", number> = {
    math: 0,
    reading: 0,
    other: 0,
  };
  for (const id of introducedTodaySkillIds ?? []) {
    newIntroCountByStrand[strandForSkill(id)] += 1;
  }
  if (chapters.length === 0) {
    return { focalSkillId: null, chapterId: null, rationale: "no_progression_state" };
  }
  // Fresh-learner signal: zero recorded SkillStates. The wrapping
  // useTodaysMission uses this to fall back to recommendStartingSkill
  // (which considers parent priors + age), since buildTodaysMission's
  // band-driven pick would deterministically pick ch1's first milestone
  // regardless of priors — undoing the work T1.1a did to wire priors
  // into the recommendation.
  if (Object.keys(states).length === 0) {
    return { focalSkillId: null, chapterId: null, rationale: "no_progression_state" };
  }

  // MA-88: precompute age-mastery set before the chapter walk so the
  // vacuous-completion guard in chapterComplete can check if a child has
  // genuinely aged past a chapter's milestone skills (AGE_MASTERY_GAP=4).
  // Absent selectionAge → empty set → guard is a no-op (backward compat).
  const isMasterySkippedFn = masterySkippedPredicate(selectionAge);
  // Hoisted out of the per-chapter block below (MA-89) — both are pure
  // functions of selectionAge alone, not chapter-dependent.
  const floorActive = selectionAge != null && Number.isFinite(selectionAge);
  // MA-454: the SERVE ceiling's membership set — identical to the tree's
  // ageAboveBandSkillIds except for the terminal-band ("7.5+") +1 tolerance
  // (see serveAboveBandSkillIds' JSDoc; the tree deliberately keeps the
  // strict set per MA-91/ADR-042).
  const aboveBand = serveAboveBandSkillIds(selectionAge);

  // (a) + (b): collect every unlocked, not-yet-complete chapter in order.
  // MA-89: this used to stop at the FIRST such chapter and commit to it even
  // if suppression left it with nothing servable. Now the walk below TRIES
  // each candidate chapter in turn and only falls through to the global
  // fallback if NONE of them yield a pick — so a parent suppressing the sole
  // on-band skill of the active chapter advances to the next real chapter's
  // candidates (still ceiling-respecting) rather than jumping straight to
  // the unconstrained lastSeenAt/last-chapter tail.
  const targetIdxs: number[] = [];
  for (let i = 0; i < chapters.length; i++) {
    const ch = chapters[i];
    if (!isChapterUnlocked(ch, chapters, states, poolSizeFor, isMasterySkippedFn, completedChapterIds)) continue;
    const synthetic = toChapterShape(ch);
    if (!chapterComplete(synthetic, states, poolSizeFor, isMasterySkippedFn, completedChapterIds?.has(ch.id) === true)) {
      targetIdxs.push(i);
    }
  }

  for (const targetIdx of targetIdxs) {
    const ch = chapters[targetIdx];
    // AGE PLACEMENT FLOOR (Wave2-C): the set of THIS chapter's candidates that
    // sit > AGE_FLOOR_GAP bands below the child. Computed from the threaded
    // selectionAge (empty set when age is null/omitted, so the floor no-ops).
    // It is a SOFT signal: belowFloor candidates are pushed AFTER at/above-floor
    // ones in the ranking, but are NOT removed — if every reachable candidate
    // is below floor (brand-new learner, or a chapter whose content is
    // genuinely early), the floor key is uniform and the original
    // band-then-declaration order stands, so the session is never stranded and
    // no prerequisite chain is skipped (the candidate pool is already only the
    // unlocked chapter's milestones+support — the floor merely re-orders it).
    // The floor (band-below + readiness) only applies when a real selection age
    // is threaded; absent it, EVERY candidate is floor 0 so behaviour is
    // byte-identical to the age-blind walk (no readiness reorder either).
    // floorActive/aboveBand are hoisted above the chapter-targeting loop
    // (MA-89) — both are pure functions of selectionAge only.
    const belowFloor = ageFloorBelowSkillIds(selectionAge);
    // Within this chapter: pick the lowest-band milestone first, then
    // lowest-band support. Declaration order is the tiebreaker. The age floor
    // is the PRIMARY key (at/above-floor before below-floor) so an older child
    // gets an age-appropriate focal skill ahead of a far-below-age one, while
    // the mastery-band ordering still governs WITHIN each floor tier.
    const ordered = [...ch.milestoneSkills, ...ch.supportSkills];
    // MA-89: suppressed ids are removed from the candidate pool BEFORE
    // ranking/eligibility — a hard exclusion, not a re-rank.
    const available = ordered.filter((id) => !suppressed.has(id));
    // MA-86 SERVE CEILING (ADR-041) — symmetric to the below-band age-cover
    // FLOOR. When a real selection age is threaded (floorActive), the focal
    // pick is HARD-capped to on-band-or-below skills: a child is NEVER served
    // a skill ABOVE their own age band while any on-band-or-below candidate
    // exists in this chapter. (The pre-MA-86 `floor` key only SOFT-
    // deprioritised above-band skills, so a zero-pool on-band frontier or an
    // all-above-band chapter could still leak an above-band serve — the
    // latent over-reach this closes.) The cap LIFTS only when this chapter has
    // NO on-band-or-below candidate AT ALL (an all-above-band chapter a child
    // has legitimately advanced into — every lower band mastered); then the
    // above-band set is served, servable-first, so the function stays total
    // and a genuinely-advanced learner progresses. A merely-zero-pool on-band
    // set does NOT lift the cap — it surfaces the on-band frontier rather than
    // papering over a content gap with a too-hard serve. The existing soft
    // `floor` key still ORDERS within the eligible set; the cap is a
    // MEMBERSHIP gate above it. Assigned-skill serving is upstream
    // (useTodaysMission) and bypasses this cap, so a deliberately-assigned
    // above-band skill still serves. floorActive=false → eligibleIds===ordered
    // → byte-identical to the pre-MA-86 walk.
    // MA-89: the cap-lift condition is evaluated on `ordered` (PRE-suppression),
    // not `available` — whether the cap lifts must depend ONLY on whether real
    // on-band-or-below content exists in this chapter, never on whether a
    // parent suppressed it. A parent removing the sole on-band skill must
    // NOT read as "genuinely advanced past all on-band content" and serve an
    // above-band skill instead — that would violate "the suppress respects
    // the MA-86 ceiling" (a suppressed skill is a removal, not a mastery
    // signal). `eligibleIds` (the actual candidates ranked/served) IS
    // suppression-filtered via `available`.
    const onBandOrBelowIds = floorActive
      ? ordered.filter((id) => !aboveBand.has(id))
      : ordered;
    const eligibleIds = floorActive
      ? available.filter((id) => !aboveBand.has(id))
      : available;
    // ── MA-86 MASTERY LIFT (MA-453) ────────────────────────────────────
    // The ceiling above is age-gated, which left MA-86's OWN item text
    // unimplemented: "an all-on-band-secured chapter admits its +1-band
    // gating milestone into the focal set". Without it, a child who has
    // CREDITED every on-band-or-below candidate of the active chapter can
    // only review — the chapter's above-band milestone (e.g. ms.read.13 for
    // a 6.x child) never serves until a birthday. The lift admits the
    // chapter's +1-band candidates into the focal set ONLY when the child
    // has genuinely exhausted the on-band frontier by CHAPTER CREDIT —
    // chapterComplete's own predicate (skillHasChapterCredit: secure/fluent
    // AND, where earnable, the MA-297 cross-day confirm), deliberately
    // chosen over plain secure for four reasons: (1) purpose-match — the
    // lift answers "is the chapter's on-band frontier DONE by the same bar
    // that would complete the chapter?", not "is it momentarily secure?";
    // (2) no drift surface — one shared exported predicate, no second
    // inline re-implementation to diverge; (3) stability — the MA-297
    // confirmedSecure gate (G2a) makes the lift latch on cross-day evidence
    // instead of flapping with a same-day secure that a demotion can
    // revoke hours later; (4) age-mask carry — withAgeImpliedMastery's
    // synthetic states set confirmedSecure=true, so age-masked skills count
    // as credited here exactly as they do in chapterComplete.
    // requireConfirm mirrors chapterComplete's own derivation for THIS
    // chapter (CONFIRMED_SECURE_CHAPTER_GATE && not already credited).
    const requireConfirm =
      CONFIRMED_SECURE_CHAPTER_GATE && completedChapterIds?.has(ch.id) !== true;
    const childRank = floorActive
      ? ageBandRank(ageBandForStudent(selectionAge))
      : -1;
    // Evaluated on the PRE-suppression onBandOrBelowIds (the MA-89 posture
    // above): a suppressed-but-UNSECURED on-band candidate must BLOCK the
    // lift — a parent removal is not a mastery signal, so it can never
    // manufacture "frontier exhausted" and pull an above-band serve forward.
    const onBandFrontierExhausted =
      floorActive &&
      onBandOrBelowIds.length > 0 &&
      onBandOrBelowIds.every((id) => skillHasChapterCredit(id, states, requireConfirm));
    // Admission (from `available`, POST-suppression, so a suppressed
    // above-band skill can never resurface through the lift):
    //   - above the child's band per the serve ceiling set, AND
    //   - within the +1 NET-CURRENCY CAP (skillAgeBandRank <= childRank+1 —
    //     the live-QA net's belowBand currency passes exactly childRank+1;
    //     note the legacy "7.0+" placeholder at rank 6 makes a 6.5-7.0
    //     child's 7.0-7.5 milestone read +2, so it is NOT admitted —
    //     documented, conservative, net-consistent), AND
    //   - renderable (poolSizeFor, when threaded), AND
    //   - prerequisite-ready (never lift past an unmet prereq), AND
    //   - not itself already credited (the lift serves NEW work, not review).
    const liftAdmitted: Set<string> = onBandFrontierExhausted
      ? new Set(
          available.filter(
            (id) =>
              aboveBand.has(id) &&
              // MA-772: rank -1 is "unknown band", not "lowest" — it trivially
              // passes the cap, so it is excluded explicitly (an unknown-band
              // skill is never lift-admitted).
              skillAgeBandRank(id) >= 0 &&
              skillAgeBandRank(id) <= childRank + 1 &&
              (!poolSizeFor || poolSizeFor(id) > 0) &&
              prerequisitesSatisfied(id, (pid) => states[pid]?.masteryBand) &&
              !skillHasChapterCredit(id, states, requireConfirm),
          ),
        )
      : new Set<string>();
    // Rank a set of candidate skill ids by the standard
    // placement -> age-floor -> mastery-band -> declaration-order key.
    // Extracted as a helper (MA-86) so it can run over the on-band-or-below
    // ELIGIBLE set and, only on a cap lift, over the above-band set.
    // (Closes over liftAdmitted — declared above — for the floor-key
    // exemption below.)
    const evidenceFloorSkillIds = new Set<string>(
      PLACEMENT_FLOOR_ENABLED
        ? [placementFloors?.reading, placementFloors?.math].filter((f): f is PlacementFloor => !!f && !f.belowOnly).map((f) => f.skillId)
        : [],
    );
    const rankCandidates = (ids: readonly string[]) => {
      // MA-851: the placement key's engagement exemption ("a played skill keeps the
      // preferred key") protects work in progress. When EVERY unfinished candidate
      // of the ranked set sits below a floor (a child whose floors cover the whole
      // active chapter: ages 4-5 with a diagnostic-raised reading and math floor),
      // the only preferred key left is a played skill that already has chapter
      // credit, and it is re-served for ever (measured: one skill, 29 of 30 days).
      // In exactly that case a finished skill is just another below-floor skill:
      // it takes the deprioritised key and the unfinished ones, by band, go first.
      // Only under an EVIDENCE reading floor (the case the mission's redirect naming below
      // serves): a below-only floor (MA-838) and a child with no reading floor keep the exemption.
      const finished = (id: string) => skillHasChapterCredit(id, states, requireConfirm);
      const evidenceReadingFloor = !!placementFloors?.reading && !placementFloors.reading.belowOnly;
      const hasOpenPreferred = ids.some((id) => !finished(id) && placementRerankKey(id, placementFloors, states) === 0);
      // per strand: a finished skill loses the exemption only against an EVIDENCE floor of its own strand
      // (a below-only math floor next to an evidence reading floor keeps the exemption for math skills)
      const endExemption = evidenceReadingFloor && !hasOpenPreferred
        ? (id: string) => {
            const st = strandForSkill(id);
            const f = st === "math" || st === "reading" ? placementFloors?.[st] : null;
            return !!f && !f.belowOnly && finished(id);
          }
        : undefined;
      return ids
      .map((skillId, idx) => ({
        skillId,
        idx,
        // PR-E placement key (PRIMARY, above the age floor: placement > age).
        // 0 for every candidate when no placementFloors are threaded, so the
        // sort below is byte-identical to the pre-PR-E age-only ranking.
        placement: placementRerankKey(skillId, placementFloors, states, endExemption),
        // MA-775: 0 = the tree does not show this skill as locked, 1 = it
        // does ("Ahead"). Under the placement key (an assessed floor still
        // wins) and above the readiness `floor`, MA-299's `newIntroCap` and
        // the mastery band. Outranking those is harmless: a tree-locked
        // candidate is always UNSTARTED (any attempt makes it "practicing"
        // unless it is above band), so among unlocked candidates the
        // readiness and new-skill-cap keys still order exactly as before.
        treeLocked: treeLockedIds?.has(skillId) ? 1 : 0,
        // 0 = at/above floor (preferred), 1 = below floor (deprioritised).
        // READINESS GUARD: a skill earns the at/above-floor (0) preference only
        // if its prerequisites are mastered. Otherwise the floor would promote
        // a skill ABOVE its own unmet prerequisite (e.g. float a no-progress
        // 6yo from ms.read.01 up to ms.read.03, whose prereq is ms.read.01).
        // Not-ready skills are deprioritised (1) so the natural band/declaration
        // order keeps the prerequisite first.
        // PROMOTED (0) only when the floor is active AND the candidate is
        // prereq-ready AND sits in the age-appropriate window: not below the
        // floor, and not above the child's own band. Everything else stays 1,
        // so the natural band/declaration order (which respects prerequisites)
        // governs and no skill is ever floated above an unmet prereq or past
        // the child's age.
        // MA-453: a mastery-LIFTED id is exempt from the above-band floor
        // penalty — without the exemption an admitted +1-band candidate
        // ranks floor=1 behind every credited floor=0 on-band skill and
        // never actually serves (the lift would be a no-op).
        // MA-837: the PLACED skill itself (an evidence floor's own skill) keeps
        // the (0) preference while it has no chapter credit, even when it sits
        // below the age floor. Without it a 6.5-year-old with an evidence
        // math.01 floor was handed the serve-covered ms.read.03 (floor 0, band
        // fluent) over the unstarted placed skill ms.math.01 (floor 1, band
        // new) on every day, so neither advanced. Only the floor skill, only
        // while uncredited: no evidence floor (every unplaced child) -> as before.
        floor:
          (floorActive &&
            !belowFloor.has(skillId) &&
            (!aboveBand.has(skillId) || liftAdmitted.has(skillId)) &&
            prerequisitesSatisfied(skillId, (id) => states[id]?.masteryBand)) ||
          (evidenceFloorSkillIds.has(skillId) &&
            !aboveBand.has(skillId) &&
            prerequisitesSatisfied(skillId, (id) => states[id]?.masteryBand) &&
            !skillHasChapterCredit(skillId, states, requireConfirm))
            ? 0
            : 1,
        band: bandRank(states[skillId]?.masteryBand ?? "new"),
        // MA-299: 0 = preferred, 1 = deprioritised. Fires ONLY for a
        // zero-attempt ("new") candidate once its OWN strand has already
        // introduced NEW_SKILL_DAILY_CAP skills today — overriding band's
        // usual "new"=0 top preference for exactly this case. A candidate
        // with any real attempt history (attemptsTotal>0) is never affected,
        // so a skill that received its first attempt minutes ago can't
        // self-suppress. Soft (sort-only, never removes a candidate) — an
        // all-new chapter still resolves a pick via band/idx tiebreak.
        newIntroCap:
          newIntroCountByStrand[strandForSkill(skillId)] >= NEW_SKILL_DAILY_CAP &&
          (states[skillId]?.attemptsTotal ?? 0) === 0
            ? 1
            : 0,
      }))
      .sort(
        (a, b) =>
          (a.placement - b.placement) ||
          (a.treeLocked - b.treeLocked) ||
          (a.floor - b.floor) ||
          (a.newIntroCap - b.newIntroCap) ||
          (a.band - b.band) ||
          (a.idx - b.idx),
      );
    };
    // MA-193: ranking-only pool check — a support skill defers to the
    // audience-aware count (so a scaffolded-only support skill like
    // ms.read.04/07 can rank as servable); a milestone skill (or any
    // caller without supportServePoolSizeFor) keeps the bare `poolSizeFor`
    // exactly as before. Never used for gating (isChapterUnlocked /
    // chapterComplete above use the untouched `poolSizeFor`).
    const servePoolSizeFor =
      supportServePoolSizeFor
        ? (id: string) =>
            ch.supportSkills.includes(id)
              ? supportServePoolSizeFor(id)
              : (poolSizeFor ? poolSizeFor(id) : 0)
        : poolSizeFor;
    // RC-R: prefer the first ranked candidate the content pipeline can
    // actually serve; when all are zero-pool (or no poolSizeFor), fall back to
    // the ungated [0] so the function stays total.
    let pick: ReturnType<typeof rankCandidates>[number] | undefined;
    if (eligibleIds.length > 0) {
      // MA-453: the mastery-lifted +1-band candidates join the eligible set
      // (disjoint by construction — liftAdmitted ⊆ aboveBand, eligibleIds
      // excludes aboveBand). Empty liftAdmitted → byte-identical to before.
      const rankedEligible = rankCandidates([...eligibleIds, ...liftAdmitted]);
      pick =
        (servePoolSizeFor
          ? rankedEligible.find((r) => servePoolSizeFor(r.skillId) > 0)
          : undefined) ?? rankedEligible[0];
    } else if (onBandOrBelowIds.length === 0 && available.length > 0) {
      // Genuine cap lift: this chapter has NO on-band-or-below candidate at
      // all (an all-above-band chapter a child has legitimately advanced
      // into), independent of suppression. `available` still excludes any
      // suppressed above-band skill, so a suppressed one can't resurface here.
      // MA-454 BOUND: the old cap lift was UNCONDITIONAL — it could serve a
      // +2/+3-band skill, which the live-QA net's belowBand currency would
      // FAIL (servedBand > childRank+1 is a net-red, not a behavior worth
      // preserving). The lift is now bounded to the same +1 net-currency cap
      // the mastery lift uses; when nothing in the chapter is within +1,
      // `pick` stays undefined and the walk falls through to the next
      // chapter / the existing fully-suppressed fallback path.
      const boundedAbove = available.filter(
        (id) => !floorActive || skillAgeBandRank(id) <= childRank + 1,
      );
      if (boundedAbove.length > 0) {
        const rankedAbove = rankCandidates(boundedAbove);
        pick =
          (servePoolSizeFor
            ? rankedAbove.find((r) => servePoolSizeFor(r.skillId) > 0)
            : undefined) ?? rankedAbove[0];
      }
    }
    // else: either every on-band-or-below candidate in this chapter is
    // suppressed (onBandOrBelowIds.length > 0 but eligibleIds is empty), or
    // the whole chapter (incl. any above-band skills) is suppressed
    // (available.length === 0). Neither lifts the cap. `pick` stays
    // undefined and the `for` loop above tries the NEXT unlocked-incomplete
    // chapter (if any); only once every candidate chapter comes up empty
    // does execution reach the global fallback below, which is ALSO
    // suppression-filtered (see the `suppressed` filter on the lastSeenAt
    // scan) so a fully-suppressed chapter can never re-serve one of its own
    // suppressed skills, on-band or above. Real chapters carry >=2-3 skills
    // (ADR-041 §2), so a parent suppressing every one is an edge case, not
    // the common path.
    if (pick) {
      return {
        focalSkillId: pick.skillId,
        chapterId: ch.id,
        rationale: "lowest_band_in_current_chapter",
      };
    }
  }

  // MA-839 END-OF-RUNWAY ADVANCE. Every unlocked chapter is complete (a 7.5-year-old's
  // chapters are age-masked, and ch4 completes at 70% support credit), yet a skill
  // inside a chapter can still be unstarted or secure-but-unconfirmed (MA-297: the
  // cross-day confirm). The tail below re-serves the MOST RECENTLY SEEN skill, which
  // is the one just played, so the child replayed the fluent, confirmed ms.math.25
  // for 17-20 days while ms.math.26/27/28 (secure, unconfirmed) were never revisited.
  // Serve the first such skill instead: within the serve ceiling, renderable,
  // prerequisite-ready, not suppressed, no chapter credit under the confirm gate
  // (requireConfirm=true whatever a persisted chapter credit says: a confirm is what
  // this serves). Lowest mastery band first, then chapter/declaration order. Nothing
  // to serve -> the tail below, unchanged.
  if (targetIdxs.length === 0) {
    let advance: { skillId: string; chapterId: string; band: number; order: number } | null = null;
    let order = 0;
    for (const ch of chapters) {
      for (const skillId of [...ch.milestoneSkills, ...ch.supportSkills]) {
        order += 1;
        if (suppressed.has(skillId)) continue;
        if (floorActive && aboveBand.has(skillId)) continue;
        const pool = supportServePoolSizeFor && ch.supportSkills.includes(skillId)
          ? supportServePoolSizeFor(skillId)
          : (poolSizeFor ? poolSizeFor(skillId) : 1);
        if (pool <= 0) continue;
        if (!prerequisitesSatisfied(skillId, (pid) => states[pid]?.masteryBand)) continue;
        if (skillHasChapterCredit(skillId, states, true)) continue;
        const band = bandRank(states[skillId]?.masteryBand ?? "new");
        if (!advance || band < advance.band) advance = { skillId, chapterId: ch.id, band, order };
      }
    }
    if (advance) {
      return { focalSkillId: advance.skillId, chapterId: advance.chapterId, rationale: "runway_unconfirmed_after_chapters_complete" };
    }

    // MA-849 END-OF-RUNWAY REVIEW ROTATION. The advance above found nothing: every chapter skill the
    // child can be served is confirmed (or not servable). The tail below would re-serve the MOST
    // RECENTLY SEEN skill, i.e. yesterday's, which picks itself until the end of the walk (measured:
    // a 7.5-year-old replayed the fluent, confirmed ms.math.28 for 10-16 days at one session a day, a
    // 7-year-old ms.read.21 for 38-51 days at two). Rotate instead: the confirmed chapter skill with the
    // OLDEST lastSeenAt (ties: chapter/declaration order, the strict `<` keeps the first). Same
    // eligibility as the advance (not suppressed, inside the serve ceiling, renderable, prerequisite-
    // ready) plus real evidence of play (an age-masked skill has no lastSeenAt, so it is never a review
    // pick, as in the tail) and chapter credit under the confirm gate. No new string: the rationale
    // field is not rendered. Nothing eligible -> the tail below, unchanged.
    // BAND PREFERENCE (RED r1 S1-1): a rotation day is a Today focal session, and the live-QA net FAILs a focal
    // served below the child's band (belowBand; only spaced_review items are exempt). So candidates on or above
    // the child's band go first; a below-band one (e.g. an evidence-floor ms.read.12 for a 7.5-year-old) is
    // chosen only when nothing at or above the band is eligible.
    const reviewChildRank = floorActive ? ageBandRank(ageBandForStudent(selectionAge)) : -1;
    let review: { skillId: string; chapterId: string; ts: string } | null = null;
    let reviewBelow: { skillId: string; chapterId: string; ts: string } | null = null;
    for (const ch of chapters) {
      for (const skillId of [...ch.milestoneSkills, ...ch.supportSkills]) {
        if (suppressed.has(skillId)) continue;
        const ts = states[skillId]?.lastSeenAt;
        if (!ts) continue;
        if (floorActive && aboveBand.has(skillId)) continue;
        const pool = supportServePoolSizeFor && ch.supportSkills.includes(skillId)
          ? supportServePoolSizeFor(skillId)
          : (poolSizeFor ? poolSizeFor(skillId) : 1);
        if (pool <= 0) continue;
        if (!prerequisitesSatisfied(skillId, (pid) => states[pid]?.masteryBand)) continue;
        if (!skillHasChapterCredit(skillId, states, true)) continue;
        if (floorActive && skillAgeBandRank(skillId) < reviewChildRank) {
          if (!reviewBelow || ts < reviewBelow.ts) reviewBelow = { skillId, chapterId: ch.id, ts };
          continue;
        }
        if (!review || ts < review.ts) review = { skillId, chapterId: ch.id, ts };
      }
    }
    review = review ?? reviewBelow;
    if (review) {
      return { focalSkillId: review.skillId, chapterId: review.chapterId, rationale: "review_rotation_end_of_runway" };
    }
  }

  // (b) all chapters complete (or none have skills): pick the first
  // milestone of the last chapter as the re-entry stone.
  // (c) fallback: most-recent lastSeenAt across all chapters.
  // MA-89: this fallback is suppression-filtered (`!suppressed.has`) so a
  // fully-suppressed chapter can't hand back a skill the parent just
  // removed. It is NOT ceiling-filtered — this is the genuine "everything
  // reachable is complete" tail, where the most-recently-practiced skill
  // (possibly above the child's CURRENT age band, e.g. a legitimately
  // advanced learner) is the correct re-entry anchor. The MA-86/89 ceiling
  // is enforced earlier, in the per-chapter walk above, which now tries
  // EVERY unlocked-incomplete chapter (not just the first) before falling
  // through here — so a suppressed on-band skill in the active chapter
  // routes to the NEXT chapter's real candidates first, not straight to
  // this unconstrained tail.
  let mostRecent: { skillId: string; chapterId: string; ts: string } | null = null;
  for (const ch of chapters) {
    for (const skillId of [...ch.milestoneSkills, ...ch.supportSkills]) {
      if (suppressed.has(skillId)) continue;
      const s = states[skillId];
      if (!s?.lastSeenAt) continue;
      if (!mostRecent || s.lastSeenAt > mostRecent.ts) {
        mostRecent = { skillId, chapterId: ch.id, ts: s.lastSeenAt };
      }
    }
  }
  if (mostRecent) {
    return {
      focalSkillId: mostRecent.skillId,
      chapterId: mostRecent.chapterId,
      rationale: "most_recent_in_progress",
    };
  }

  // (b') everything complete (or empty), no state: the last chapter's
  // first milestone. MA-89: skip a suppressed one (same rationale as the
  // lastSeenAt scan above — never hand back a parent-removed skill).
  const last = chapters[chapters.length - 1];
  const firstAvailableMilestone = last.milestoneSkills.find((id) => !suppressed.has(id));
  if (firstAvailableMilestone) {
    return {
      focalSkillId: firstAvailableMilestone,
      chapterId: last.id,
      rationale: "first_stone_of_next_chapter",
    };
  }

  // (d) absolute-empty — caller falls back to recommendStartingSkill.
  return { focalSkillId: null, chapterId: null, rationale: "no_progression_state" };
}

/**
 * The mission, with MA-851's one naming rule: a pick the /play/foundation guard
 * would redirect (a foundation skill strictly below the reading floor) is named as
 * the redirect target, the floor skill, in the floor skill's own chapter. Today, the
 * CTA and the Journey tap then agree. Without it, the placement key's finished-skill
 * rule above lets the walk name ms.read.01 for a child whose floor is ms.read.06 and
 * the guard sends the CTA to ms.read.06 while Today still says ms.read.01.
 * The ranker keeps picking inside the active chapter (chapter order still gates the
 * walk); only the NAME of an otherwise-redirected pick changes. No floor, or a pick
 * the guard lets through: the pick is returned untouched.
 */
export function buildTodaysMission(...args: Parameters<typeof buildTodaysMissionPick>): TodaysMissionPick {
  const pick = buildTodaysMissionPick(...args);
  const floor = args[4]?.reading;
  // An evidence floor only: a below-only floor (MA-838) is not the Journey's served stone, so the
  // stone of the skill it would name could not be opened; its pick is left to the guard, as before.
  const target = pick.focalSkillId && floor && !floor.belowOnly ? foundationRedirectSkillId(pick.focalSkillId, floor, args[10] ?? placementValveLowered(floor, args[1])) : null;
  if (!floor || !target) return pick;
  // Invariant (MA-851 N6): the mission never names a skill the /play/foundation guard redirects. There is NO
  // work-in-progress exemption here (the ranker's engagement exemption decides the PICK; the NAME follows the guard):
  // an in-progress skill from another device would otherwise stay Today's focal while the CTA lands on the floor skill.
  // A LOWERED demotion valve is handled in the target above: the guard then lets the floor skill's own
  // prerequisites through (placementFloor.guardAllowsBelowFloor), so the ranker's pick for an over-placed child
  // is the skill Today names and the CTA opens.
  const chapter = args[0].find((c) => c.milestoneSkills.includes(target) || c.supportSkills.includes(target));
  return { ...pick, focalSkillId: target, chapterId: chapter?.id ?? pick.chapterId };
}

// ── MA-956: a due review in an unpractised strand brings that strand back to Today ──
//
// MA-896 made a tree-secure skill whose delayed review has not passed a due review candidate, but
// the review slot only ever serves a skill of the SESSION's strand (pickReviewSkill, same strand as
// the focal), and FoundationSession has no SessionRun and so no review slot. At one session a day the
// Today CTA can stay in one strand (or on Foundation sessions) for weeks, so the candidate is never
// reached. The rule (operator decision on MA-956): when a strand holds such a due candidate and none
// of the child's last UNPRACTISED_STRAND_WINDOW_DAYS practice days had a Math or Reading session of
// that strand, the mission's focal comes from that strand (the existing ranker, other strands and
// Foundation-routed skills excluded through its own suppressed-set key), so the CTA lands on that
// strand's Math or Reading session and its review slot can serve the candidate. Slot counts, the
// review pick and the due-tier order are untouched (buildSessionRun / pickReviewSkill unchanged).
// With no such candidate the pick is the unchanged buildTodaysMission pick.

/** MA-956: how many of the child's most recent practice days are looked at. */
export const UNPRACTISED_STRAND_WINDOW_DAYS = 2;

/** MA-956: a row written by a Math or Reading session (a screen with a SessionRun and so a review
 *  slot): a path row that is not FoundationSession's (audienceMode "scaffolded", FoundationSession.tsx
 *  :565) and not MixedSession's (adaptiveReason "cross_strand", MixedSession.tsx:878/934; the Math and
 *  Reading screens map cross_strand to the focal role). A legacy row without audienceMode counts as a
 *  Math or Reading session row (fail-closed: the strand reads as practised, so no override). */
function isMathOrReadingSessionRow(a: ChildAttempt): boolean {
  return (
    (a.sessionSource ?? "path") === "path" &&
    a.audienceMode !== "scaffolded" &&
    a.adaptiveReason !== "cross_strand"
  );
}

/** MA-956: the unpractised strand that holds a due MA-896 review candidate. */
export type UnpractisedStrandReview = {
  strand: "math" | "reading";
  /** That strand's due candidates, in the due tier's order (the first decided the strand). */
  dueSkillIds: string[];
  /** The review pick the strand's session would make with `focal` (buildSessionRun on the same
   *  states, gate and clock — the producer the screens call), or null. */
  reviewPickFor: (focal: string) => string | null;
};

/** MA-956: the strand whose due MA-896 review candidate has gone unpractised, or null.
 *  `attempts` = the merged attempt log the mission already reads; `now` = the mission's clock
 *  (threaded, never read here); `reviewPoolSizeFor` = the review slot's own renderability gate per
 *  skill (Math: servedItemCountForSkill, Reading: readingReviewPoolSize — the gates the screens pass
 *  buildSessionRun), so a candidate the slot could not serve never moves the CTA.
 *  A candidate: math or reading, stamped tree-review-pending by the aggregator (the MA-896 bridge)
 *  and due by the review slot's own predicate (isDueForReviewSlot: independent_practice audience,
 *  last seen on an earlier day). Unpractised: the child has at least UNPRACTISED_STRAND_WINDOW_DAYS
 *  practice days (local days with a path row, today included) and none of the most recent
 *  UNPRACTISED_STRAND_WINDOW_DAYS of them has a Math or Reading session row of that strand.
 *  Both strands qualifying: the strand of the candidate the due tier orders first (dueTierRank,
 *  oldest lastSeenAt, skillKey). Pure. */
export function unpractisedStrandWithDueReview(
  attempts: readonly ChildAttempt[],
  now: Date | null | undefined,
  reviewPoolSizeFor: ((skillId: string) => number) | undefined,
  sessionLength: number,
  selectionAge?: number | null,
): UnpractisedStrandReview | null {
  if (!(now instanceof Date) || !Number.isFinite(now.getTime())) return null;
  const today = dayKey(now.toISOString());
  const pathRows = attempts.filter(
    (a) => (a.sessionSource ?? "path") === "path" && typeof a.timestamp === "string" && dayKey(a.timestamp) <= today,
  );
  const practiceDays = [...new Set(pathRows.map((a) => dayKey(a.timestamp)))]
    .sort()
    .reverse()
    .slice(0, UNPRACTISED_STRAND_WINDOW_DAYS);
  if (practiceDays.length < UNPRACTISED_STRAND_WINDOW_DAYS) return null;
  const windowDays = new Set(practiceDays);
  const practised = new Set<string>();
  for (const a of pathRows) {
    if (windowDays.has(dayKey(a.timestamp)) && isMathOrReadingSessionRow(a)) practised.add(strandForSkill(a.skillId));
  }
  if (practised.has("math") && practised.has("reading")) return null;
  // The states the session screens hand buildSessionRun (every attempt, the MA-896 stamp included).
  const states = aggregateSkillStatesFromAttempts([...attempts]);
  const childRank = selectionAge != null && Number.isFinite(selectionAge) ? ageBandRank(ageBandForStudent(selectionAge)) : -1;
  const due = Object.values(states).filter((s) => {
    const strand = strandForSkill(s.skillKey);
    return (
      (strand === "math" || strand === "reading") &&
      !practised.has(strand) &&
      // Never a skill below the child's band (it would add below-band reviews).
      !isBelowChildBand(s.skillKey, childRank) &&
      isTreeReviewPending(s) &&
      isDueForReviewSlot(s, today) &&
      (!reviewPoolSizeFor || reviewPoolSizeFor(s.skillKey) > 0)
    );
  });
  if (due.length === 0) return null;
  const seenMs = (iso: string | null): number => {
    const t = iso ? Date.parse(iso) : NaN;
    return Number.isNaN(t) ? 0 : t;
  };
  due.sort((a, b) => {
    const r = dueTierRank(a) - dueTierRank(b);
    if (r !== 0) return r;
    const d = seenMs(a.lastSeenAt) - seenMs(b.lastSeenAt);
    return d !== 0 ? d : a.skillKey.localeCompare(b.skillKey);
  });
  const strand = strandForSkill(due[0].skillKey) as "math" | "reading";
  return {
    strand,
    dueSkillIds: due.filter((s) => strandForSkill(s.skillKey) === strand).map((s) => s.skillKey),
    reviewPickFor: (focal) => buildSessionRun(focal, states, sessionLength, reviewPoolSizeFor, [], now).reviewSkillId,
  };
}

/** MA-956: is this skill's band below the child's band (childRank -1 ⇒ no age, never)? */
function isBelowChildBand(skillId: string, childRank: number): boolean {
  if (childRank < 0) return false;
  const r = skillAgeBandRank(skillId);
  return r >= 0 && r < childRank;
}

/** MA-956: does a focal open that strand's Math or Reading session (not FoundationSession)? PathHome
 *  routes a reading skill in FOUNDATION_SKILL_IDS (audienceForSkill "scaffolded") to /play/foundation. */
function opensStrandSession(skillId: string | null, strand: "math" | "reading"): boolean {
  if (!skillId || strandForSkill(skillId) !== strand) return false;
  return !(strand === "reading" && audienceForSkill(skillId) === "scaffolded");
}

/** The strand-picks MA-956 accepts: the chapter walk and the end-of-runway picks. The bare
 *  last-chapter first stone (no seen skill of the strand at all) is never a review-slot carrier. */
const STRAND_RETURN_RATIONALES: ReadonlySet<TodaysMissionPick["rationale"]> = new Set([
  "lowest_band_in_current_chapter",
  "runway_unconfirmed_after_chapters_complete",
  "review_rotation_end_of_runway",
  "most_recent_in_progress",
]);

/** MA-901 (operator ruling D-1013-9): when the pick is a focal skill still `emerging` that has
 *  been its strand's focal on FOCAL_ROTATION_DAYS or more consecutive practice days (the
 *  aggregator's `focalStuckDays` stamp), serve the strand's next-ranked focal skill for this
 *  day: the SAME ranker with the stuck skill added to its suppressed set. Taken only when that
 *  pick is in the same strand, from the chapter walk or the runway tail, renderable, not below
 *  the child's band and not tree-locked (args[9]: a skill whose prerequisites are not secured).
 *  Anything else keeps the stuck pick. The next day the stuck skill's run is 0 (yesterday's
 *  focal was the sibling), so the child returns to it. Nothing else changes (slots, review,
 *  the 0.85 bar). */
function rotateStuckFocal(
  args: Parameters<typeof buildTodaysMissionPick>,
  pick: TodaysMissionPick,
): TodaysMissionPick {
  const id = pick.focalSkillId;
  if (!id) return pick;
  const st = args[1]?.[id] as FocalRunStampedSkillState | undefined;
  if (!st || st.masteryBand !== "emerging" || (st.focalStuckDays ?? 0) < FOCAL_ROTATION_DAYS) return pick;
  // The strand's ranker: the stuck skill and every chapter skill of another strand are added to
  // the suppressed set (the MA-956 strand-return pattern). A rejected candidate is excluded and
  // the ranker asked again, at most ROTATION_TRIES times.
  const strand = strandForSkill(id);
  const excluded = new Set<string>(args[5] ?? []);
  excluded.add(id);
  for (const ch of args[0]) {
    for (const sid of [...ch.milestoneSkills, ...ch.supportSkills]) {
      if (strandForSkill(sid) !== strand) excluded.add(sid);
    }
  }
  const selectionAge = args[3];
  const childRank = selectionAge != null && Number.isFinite(selectionAge) ? ageBandRank(ageBandForStudent(selectionAge)) : -1;
  const locked = args[9] as ReadonlySet<string> | null | undefined;
  // Fail closed (RED round 2 SHOULD-1, D-1013-12): without a selection age or a tree-lock read the
  // guards cannot be checked, so the child keeps the stuck skill.
  if (childRank < 0 || locked == null || typeof locked.has !== "function") return pick;
  const servedPool = args[6] ?? args[2];
  for (let tries = 0; tries < ROTATION_TRIES; tries++) {
    const altArgs = [...args] as Parameters<typeof buildTodaysMissionPick>;
    altArgs[5] = excluded;
    const alt = buildTodaysMission(...altArgs);
    const altId = alt.focalSkillId;
    if (!altId || excluded.has(altId) || strandForSkill(altId) !== strand || !STRAND_RETURN_RATIONALES.has(alt.rationale)) return pick;
    if (
      // Renderable by the session its CTA opens: the audience-aware pool (args[6], the one the
      // ranker serves support skills by; a Foundation-routed skill is served scaffolded), else args[2].
      (servedPool && servedPool(altId) <= 0) ||
      // Never below the child's band; never a skill the tree shows locked (prerequisites not secured).
      isBelowChildBand(altId, childRank) ||
      locked.has(altId)
    ) {
      excluded.add(altId);
      continue;
    }
    return alt;
  }
  return pick;
}
const ROTATION_TRIES = 8;

/** MA-956: buildTodaysMission with the unpractised-strand review return. `review` =
 *  unpractisedStrandWithDueReview(...) (null ⇒ exactly buildTodaysMission(...args)). When the
 *  pick already opens that strand's Math or Reading session and is not itself a due candidate it is
 *  returned untouched. Otherwise the SAME ranker runs again with the same arguments, with added to
 *  its suppressed set (its hard membership key, MA-89): every chapter skill of the other strand,
 *  every Foundation-routed skill, and the strand's due candidates (the review slot never serves the
 *  focal, pickReviewSkill, so a candidate as the focal would not get its review). That pick is
 *  taken only if it opens the strand's session, has a served pool (`poolSizeFor`, args[2]) and came
 *  from the chapter walk or the runway tail. Anything else returns the unchanged pick. */
export function buildTodaysMissionWithStrandReview(
  args: Parameters<typeof buildTodaysMissionPick>,
  review: UnpractisedStrandReview | null,
): TodaysMissionPick {
  const pick = rotateStuckFocal(args, buildTodaysMission(...args));
  if (review == null) return pick;
  const { strand, dueSkillIds } = review;
  if (opensStrandSession(pick.focalSkillId, strand) && !dueSkillIds.includes(pick.focalSkillId as string)) return pick;
  const excluded = new Set<string>(args[5] ?? []);
  for (const id of dueSkillIds) excluded.add(id);
  for (const ch of args[0]) {
    for (const id of [...ch.milestoneSkills, ...ch.supportSkills]) {
      if (!opensStrandSession(id, strand)) excluded.add(id);
    }
  }
  const strandArgs = [...args] as Parameters<typeof buildTodaysMissionPick>;
  strandArgs[5] = excluded;
  const strandPick = buildTodaysMission(...strandArgs);
  const poolSizeFor = args[2];
  const selectionAge = args[3];
  const childRank = selectionAge != null && Number.isFinite(selectionAge) ? ageBandRank(ageBandForStudent(selectionAge)) : -1;
  if (
    !opensStrandSession(strandPick.focalSkillId, strand) ||
    !STRAND_RETURN_RATIONALES.has(strandPick.rationale) ||
    (poolSizeFor && poolSizeFor(strandPick.focalSkillId as string) <= 0) ||
    // A below-band focal would bring a below-band skill into the strand's later review rotation.
    isBelowChildBand(strandPick.focalSkillId as string, childRank)
  ) {
    return pick;
  }
  // The return exists to serve a candidate: the strand session's own review pick must be one.
  const reviewPick = review.reviewPickFor(strandPick.focalSkillId as string);
  if (reviewPick == null || !dueSkillIds.includes(reviewPick)) return pick;
  return strandPick;
}

/** MA-246 (ADR-068): the ratchet's WRITE side. Returns the ids of every
 *  UNLOCKED chapter that (a) is not already in `alreadyCompleted` and (b)
 *  live-derives complete right now (chapterComplete with the ratchet
 *  itself not yet applied, since it isn't credited yet). The caller
 *  (useTodaysMission) persists a `chapter_complete` milestone event for
 *  each returned id — that persisted event becomes the `alreadyCompleted`
 *  floor on every SUBSEQUENT call, protecting this chapter's support
 *  coverage from a later curriculum edit that grows `supportSkills`.
 *
 *  Scoped to UNLOCKED chapters only (same gate the chapter walk above
 *  uses) — a chapter the child hasn't even reached yet should never mint
 *  a completion credit off incidental cross-strand practice. Pure; no I/O
 *  (the event write itself is the caller's job). */
export function newlyCompletedChapterIds(
  chapters: (Chapter | ChapterForMission)[],
  states: Record<string, SkillState>,
  alreadyCompleted: ReadonlySet<string>,
  poolSizeFor?: (skillId: string) => number,
  isMasterySkipped?: (skillId: string) => boolean,
): string[] {
  const out: string[] = [];
  for (const ch of chapters) {
    if (alreadyCompleted.has(ch.id)) continue;
    if (!isChapterUnlocked(ch, chapters, states, poolSizeFor, isMasterySkipped, alreadyCompleted)) continue;
    if (chapterComplete(toChapterShape(ch), states, poolSizeFor, isMasterySkipped, false)) {
      out.push(ch.id);
    }
  }
  return out;
}

// ── Helpers consumed by the recap (the visible signal layer) ───────────

/** Count of skills whose masteryBand is "secure" or "fluent" — the
 *  research-precise "mastered" definition the recap reports. */
export function countMasteredSkills(states: Record<string, SkillState>): number {
  let n = 0;
  for (const k in states) {
    const b = states[k]?.masteryBand;
    if (b === "secure" || b === "fluent") n += 1;
  }
  return n;
}

/** Count of DURABLY-mastered skills — the band reserved for the user-facing
 *  "mastered" CLAIM (the recap's skills-mastered tile). Only "fluent" counts:
 *  per deriveMasteryBand, fluent requires >=2 sessions, >=85% accuracy AND
 *  demonstrated RETENTION (retentionConfidence >= 0.70). "secure" (accurate
 *  but retention not yet shown) is real progress but NOT yet mastery — so the
 *  recap no longer claims "+1 mastered" after a strong session that hasn't
 *  proven retention over spaced review. (countMasteredSkills, secure+fluent,
 *  is kept as the broader PROGRESS signal that drives mascot growth, so
 *  early engagement is unaffected.) */
export function countDurablyMasteredSkills(states: Record<string, SkillState>): number {
  let n = 0;
  for (const k in states) {
    if (states[k]?.masteryBand === "fluent") n += 1;
  }
  return n;
}
